import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { gsap } from 'gsap';
import {
  BadgeCheck,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  LayoutList,
  Phone,
  Skull,
  Sparkles,
  Trophy,
  TrendingUp,
  X,
  XCircle,
} from 'lucide-react';
import './GameTable.css';
import '../poker-ui/sekaAnteBurst.css';
import { NotificationToast, FlyingCoins, FlickeringElement, SessionResultOverlay } from './GameEffects';
import { OnlineSekaLeaveModal } from './OnlineSekaLeaveModal';
import { AudioProvider, useAudio } from '../poker-ui/AudioProvider';
import { GameIndustrialHeader } from '../poker-ui/GameIndustrialHeader';
import { PokerTableSurface } from '../poker-ui/PokerTableSurface';
import { IndustrialSeat } from '../poker-ui/IndustrialSeat';
import { SekaActionBar } from '../poker-ui/SekaActionBar';
import { PokerBetRaiseSlider } from '../poker-ui/PokerBetRaiseSlider';
import { LiveSekaHandMovesPanel, LiveSekaHandMovesTrail } from '../poker-ui/LiveSekaHandMovesPanel';
import { MatchChronicleChain, MatchChronicleVertical } from '../poker-ui/MatchChronicleChain';
import { PlayerHandStrip } from '../poker-ui/PlayerHandStrip';
import {
  appendSekaMatchHistoryExportRow,
  buildPlayerSnapshotsFromHistoryEntry,
  buildSimpleSekaOutcomeExportText,
  describeSekaHandEndType,
  formatHistoryAction,
  SEKA_HISTORY_EXPORT_HINT_LINE,
} from '../game/sekaHistoryUi';
import { DEFAULT_SEKA_CONFIG, evaluateSekaHand } from '../game/sekaEngine';
import { parseSekaCard } from '../poker-ui/utils/parseSekaCard';
import { cn } from '../poker-ui/utils/cn';
import { buildEllipticSeatSlots, getSeatArenaTuning, getSeatDensityPreset } from '../poker-ui/seatLayout';
import {
  clampSeatCenter,
  seatDisplayPercents,
  loadOffsetsForBlock,
  saveOffsetsForBlock,
  clearOffsetsForBlock,
  SEAT_OFFSET_STORAGE,
} from '../poker-ui/seatLayoutOffsets';
import { getApiBaseUrl } from '../lib/apiBaseUrl';
import { PlayingCard } from '../poker-ui/PlayingCard';
import { TableSeatProfileModal } from '../poker-ui/TableSeatProfileModal';
import { ColyseusSekaGameTransport } from '../realtime/colyseusSekaGameTransport';
import {
  duplicateSessionUserMessage,
  duplicateSessionSetErrorMessage,
  finalDisconnectInlineErrorText,
  finalDisconnectUserMessage,
  isGameTransportErrorNoise,
  isReconnectFallbackJoin,
  reconnectSuccessUserMessage,
} from '../realtime/connectionUx';
import { SEKA_SCHEMA_VERSION, isSekaGameStateSnapshotV1 } from '../contracts/sekaV1';

/**
 * Снимок `game/state` от Colyseus (см. server/src/contracts/sekaV1.js).
 * @typedef {import('../contracts/sekaV1').SekaGameStateSnapshotV1} SekaGameStateSnapshotV1
 */

const SERVER_ACTION_COLORS = {
  FOLD: 'red',
  LOOK: 'blue',
  CALL: 'green',
  CHECK: 'blue',
  RAISE: 'orange',
  SHOWDOWN: 'purple',
};

/** Полёт одной карты от колоды к месту (как в LocalGame). */
const ONLINE_DEAL_FLIGHT_MS = 150;
const ONLINE_DEAL_BETWEEN_PLAYERS_MS = 175;
const ONLINE_DEAL_FLIGHT_BURST_MS = Math.round(ONLINE_DEAL_FLIGHT_MS * 0.7);
const ONLINE_DEAL_CARD_SOUND_VOL = 0.78;

function OnlineCurrencyToast({ message, kind = 'info', onClose }) {
  const onCloseRef = useRef(onClose);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const leaveTimer = setTimeout(() => setLeaving(true), 2300);
    const closeTimer = setTimeout(() => onCloseRef.current?.(), 2700);
    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(closeTimer);
    };
  }, []);

  const text = String(message || '');
  const isDebit = kind === 'debit';
  const isCredit = kind === 'credit';
  const shellClass = isDebit
    ? 'border-red-400/45 from-[#3a141a]/96 via-[#281018]/96 to-[#15080c]/96 shadow-[0_12px_34px_rgba(220,38,38,0.28)]'
    : isCredit
      ? 'border-emerald-300/55 from-[#10351f]/96 via-[#0f2b1b]/96 to-[#0a1a10]/96 shadow-[0_12px_34px_rgba(34,197,94,0.34)]'
      : 'border-cyan-300/45 from-[#10364d]/96 via-[#0e283b]/96 to-[#0a1726]/96 shadow-[0_12px_32px_rgba(34,211,238,0.26)]';
  const iconClass = isDebit
    ? 'border-red-300/35 bg-red-500/18 text-rose-100'
    : isCredit
      ? 'border-emerald-300/45 bg-emerald-500/20 text-yellow-200'
      : 'border-cyan-300/35 bg-cyan-500/15 text-cyan-100';
  const toneClass = isDebit ? 'text-rose-100' : isCredit ? 'text-yellow-200' : 'text-cyan-50';
  const shift = leaving ? '-translate-y-1 scale-[0.985] opacity-0' : 'translate-y-0 scale-100 opacity-100';

  return (
    <div className="pointer-events-none fixed left-1/2 top-[max(0.7rem,env(safe-area-inset-top,0px))] z-[130] w-auto max-w-[min(calc(100%-2.25rem),22rem)] -translate-x-1/2">
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border bg-gradient-to-br px-3 py-2 backdrop-blur-md transition-all duration-300 ease-out',
          'animate-[app-toast-in_360ms_cubic-bezier(0.2,0.8,0.2,1)]',
          shellClass,
          shift
        )}
        role={isDebit ? 'alert' : 'status'}
      >
        <span className="pointer-events-none absolute inset-x-0 top-0 h-[40%] bg-gradient-to-b from-white/18 to-transparent" />
        <div className="relative z-[1] flex items-center gap-2.5">
          <span className={cn('inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border', iconClass)}>
            <BadgeCheck className="h-4.5 w-4.5" aria-hidden />
          </span>
          <p className={cn('min-w-0 break-words leading-snug text-[0.95rem] font-black tracking-[0.02em]', toneClass)}>
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}

function appendOnlineMatchChronicleBody(lines, matchChronicleHands, currentHandMoves, currentHandSeq) {
  const hands = Array.isArray(matchChronicleHands) ? matchChronicleHands : [];
  if (hands.length > 0) {
    hands.forEach((h) => {
      lines.push(`--- Раздача №${h.handSeq} ---`);
      (h.lines || []).forEach((l) => lines.push(l));
      lines.push('');
    });
  }
  lines.push('--- Текущая раздача ---');
  if (Number(currentHandSeq) > 0) {
    lines.push(`№ ${currentHandSeq}`);
  }
  const moves = Array.isArray(currentHandMoves) ? currentHandMoves : [];
  if (moves.length === 0) {
    lines.push('(ходов ещё нет)');
  } else {
    moves.forEach((m) => {
      const name = typeof m.playerName === 'string' ? m.playerName : '—';
      lines.push(`${name}: ${formatHistoryAction(m)}`);
    });
  }
}

function buildOnlineMatchChronicleExportText(matchChronicleHands, currentHandMoves, currentHandSeq) {
  const lines = [];
  lines.push('=== Сека — хроника матча (онлайн) ===');
  lines.push(`Дата: ${new Date().toLocaleString('ru-RU')}`);
  lines.push('');
  lines.push(SEKA_HISTORY_EXPORT_HINT_LINE);
  lines.push('');
  appendOnlineMatchChronicleBody(lines, matchChronicleHands, currentHandMoves, currentHandSeq);
  return lines.join('\n');
}

function serverHandSummaryInvolvesHero(h, heroUserId) {
  if (!heroUserId) return true;
  const uid = String(heroUserId);
  if ((h.winners || []).some((w) => String(w.userId) === uid)) return true;
  const row = (h.seats || []).find((s) => String(s.userId) === uid);
  if (!row) return false;
  if (Number(row.delta) !== 0) return true;
  if (Number(row.chipsBefore) > 0) return true;
  return false;
}

function appendServerCompletedHandSummaries(lines, completedHandSummaries, endTypeByHandSeq) {
  const rows = Array.isArray(completedHandSummaries) ? completedHandSummaries : [];
  if (rows.length === 0) return;
  lines.push('--- Бухгалтерия по раздачам (сервер) ---');
  rows.forEach((h) => {
    const tag = h.earlyFinish ? ' [раннее завершение]' : '';
    const seq = Number(h.handSeq);
    const gross = Number(h.potGross || 0);
    const rake = Number(h.rakeAmount || 0);
    const net = Number(h.potNet || 0);
    lines.push(
      `Раздача №${Number(h.handSeq) || '—'} · банк ${Number(h.pot || 0).toLocaleString('ru-RU')} SEKA${tag}`
    );
    if (gross > 0 || rake > 0 || net > 0) {
      lines.push(
        `  Брутто/комиссия/нетто: ${gross.toLocaleString('ru-RU')} / ${rake.toLocaleString('ru-RU')} / ${net.toLocaleString('ru-RU')} SEKA`
      );
    }
    const et =
      endTypeByHandSeq instanceof Map && Number.isFinite(seq)
        ? endTypeByHandSeq.get(seq)
        : null;
    if (et) {
      lines.push(`  Исход (тип): ${et}`);
    }
    const wn = Array.isArray(h.winners) ? h.winners : [];
    if (wn.length > 0) {
      lines.push(`  Победители: ${wn.map((x) => x.username || x.userId || '—').join(', ')}`);
    }
    const seats = Array.isArray(h.seats) ? h.seats : [];
    seats.forEach((s) => {
      const d = Number(s.delta) || 0;
      const sign = d > 0 ? '+' : '';
      lines.push(
        `  [${s.seatIndex}] ${s.username || '—'}: ${Number(s.chipsBefore || 0).toLocaleString('ru-RU')} → ${Number(s.chipsAfter || 0).toLocaleString('ru-RU')} (${sign}${d.toLocaleString('ru-RU')})`
      );
    });
    lines.push('');
  });
}

function buildOnlineFullSessionExportText({
  tableName,
  blindsLabel,
  tableBuyIn,
  sessionTotalHands,
  sessionHeroWins,
  sessionHeroLosses,
  chronicleArchivedCount,
  matchChronicleHands,
  currentHandMoves,
  currentHandSeq,
  completedHandSummaries,
  fullHandRows = [],
  /** Все раздачи из gameHistory для строки «Исход» в бухгалтерии (даже если детальный журнал отфильтрован по герою). */
  allHandRowsForEndTypeMap = null,
}) {
  const lines = [];
  lines.push('=== Сека — хроника матча (онлайн, полная сводка) ===');
  lines.push(`Дата: ${new Date().toLocaleString('ru-RU')}`);
  lines.push(SEKA_HISTORY_EXPORT_HINT_LINE);
  lines.push('');
  if (tableName) lines.push(`Стол: ${tableName}`);
  if (blindsLabel && blindsLabel !== '—') lines.push(`Лимиты: ${blindsLabel}`);
  const bi = Number(tableBuyIn);
  if (Number.isFinite(bi) && bi > 0) lines.push(`Вход за стол: ${bi.toLocaleString('ru-RU')} SEKA`);
  lines.push('');
  lines.push('--- Статистика сессии ---');
  lines.push(`Завершённых раздач (учёт): ${sessionTotalHands}`);
  lines.push(`Побед за столом: ${sessionHeroWins}`);
  lines.push(`Поражений (в раздачах с вашими ходами): ${sessionHeroLosses}`);
  lines.push(`Раздач в архиве хроники: ${chronicleArchivedCount}`);
  lines.push('');

  const endTypeByHandSeq = new Map();
  const rowsForEndType =
    Array.isArray(allHandRowsForEndTypeMap) && allHandRowsForEndTypeMap.length > 0
      ? allHandRowsForEndTypeMap
      : fullHandRows;
  (Array.isArray(rowsForEndType) ? rowsForEndType : []).forEach((r) => {
    const seq = Number(r?.handSeq);
    if (Number.isFinite(seq) && r?.endType) endTypeByHandSeq.set(seq, r.endType);
  });
  appendServerCompletedHandSummaries(lines, completedHandSummaries, endTypeByHandSeq);
  if (Array.isArray(completedHandSummaries) && completedHandSummaries.length > 0) {
    lines.push('');
  }

  lines.push('--- Раздачи: детальный журнал (gameHistory) ---');
  lines.push('');
  if (Array.isArray(fullHandRows) && fullHandRows.length > 0) {
    fullHandRows.forEach((row) => {
      appendSekaMatchHistoryExportRow(lines, row, {
        handLabelStyle: 'numero',
        stackDeltaTitle: 'Дельта за раздачу (стек на месте):',
      });
    });
  } else {
    lines.push(
      '(полных записей gameHistory в памяти клиента нет — см. бухгалтерию выше и хронику ходов ниже)'
    );
    lines.push('');
  }

  lines.push('--- Хроника ходов ---');
  lines.push('');
  appendOnlineMatchChronicleBody(lines, matchChronicleHands, currentHandMoves, currentHandSeq);
  return lines.join('\n');
}

/** Пока идёт раздача или только что показан итог (снимок с сервера даёт `finished`). */
function isOnlineTableHandSettled(table) {
  return Boolean(table && (table.state === 'playing' || table.state === 'finished'));
}

/** Синхрон с `SekaRoom._startSvarkaCommit` (мс на сбор решений). */
const ONLINE_SVARKA_COMMIT_TOTAL_MS = 7000;

function buildOnlineHandOutcomeExportText({
  tableName,
  handSeq,
  pot,
  winners,
  winner,
  handMoves,
  heroUserId,
  historyEntry = null,
}) {
  const lines = [];
  lines.push('=== Сека — итог раздачи (онлайн) ===');
  lines.push(`Время: ${new Date().toLocaleString('ru-RU')}`);
  lines.push('');
  lines.push(SEKA_HISTORY_EXPORT_HINT_LINE);
  lines.push('');
  if (tableName) lines.push(`Стол: ${tableName}`);
  lines.push(`Раздача №${handSeq > 0 ? handSeq : '—'}`);
  lines.push(`Банк: ${Number(pot || 0).toLocaleString('ru-RU')} SEKA`);
  if (historyEntry && typeof historyEntry === 'object') {
    const actions = Array.isArray(historyEntry.actions) ? historyEntry.actions : [];
    lines.push(`  Исход раздачи: ${describeSekaHandEndType(historyEntry, actions)}`);
  }
  lines.push('');
  lines.push('--- Победители ---');
  const wlist =
    Array.isArray(winners) && winners.length > 0
      ? winners
      : winner
        ? [winner]
        : [];
  if (wlist.length === 0) {
    lines.push('(нет данных)');
  } else {
    wlist.forEach((w) => {
      const name = w?.username || w?.name || '—';
      lines.push(String(name));
    });
  }
  lines.push('');
  if (heroUserId) {
    const heroWon = wlist.some((x) => x && String(x.userId) === String(heroUserId));
    lines.push('--- Вы ---');
    lines.push(heroWon ? 'Вы выиграли банк в этой раздаче.' : 'Банк достался другому игроку.');
    lines.push('');
  }
  lines.push('--- Ходы ---');
  const moves = Array.isArray(handMoves) ? handMoves : [];
  if (moves.length === 0) {
    lines.push('(ходов нет)');
  } else {
    moves.forEach((m) => {
      const name = typeof m.playerName === 'string' ? m.playerName : '—';
      lines.push(`${name}: ${formatHistoryAction(m)}`);
    });
  }
  return lines.join('\n');
}

function SeatLastMoveBadge({ action, isDimmed, compact = false }) {
  if (!action?.kind) return null;
  let Icon = Check;
  let short = '';
  let tone =
    'border-border/80 bg-muted/25 text-foreground/90 hover:border-neon-cyan/40 hover:bg-muted/45 hover:shadow-[0_0_12px_rgba(0,255,255,0.12)]';
  let iconTone = 'text-muted-foreground';

  switch (action.kind) {
    case 'fold':
      Icon = XCircle;
      short = 'Пас';
      tone = 'border-red-500/35 bg-red-950/25 text-red-100/95';
      iconTone = 'text-red-400/95';
      break;
    case 'look':
      Icon = Eye;
      short = 'Смотрит';
      tone = 'border-neon-gold/35 bg-amber-950/25 text-amber-100/95';
      iconTone = 'text-neon-gold';
      break;
    case 'call':
      Icon = Phone;
      short = 'Колл';
      tone = 'border-cyan-500/35 bg-cyan-950/20 text-cyan-50/95';
      iconTone = 'text-neon-cyan';
      break;
    case 'check':
      Icon = Check;
      short = 'Чек';
      tone = 'border-cyan-500/35 bg-cyan-950/20 text-cyan-50/95';
      iconTone = 'text-neon-cyan';
      break;
    case 'raise':
      Icon = TrendingUp;
      short = action.amount ? `Рейз ${action.amount}` : 'Рейз';
      tone = 'border-neon-orange/40 bg-orange-950/25 text-orange-50/95';
      iconTone = 'text-neon-orange';
      break;
    case 'showdown':
      Icon = Sparkles;
      short = 'Вскрытие';
      tone = 'border-violet-400/35 bg-violet-950/25 text-violet-100/95';
      iconTone = 'text-violet-300';
      break;
    default:
      short = action.kind;
  }

  return (
    <div
      className={cn(
        'flex max-w-[92px] cursor-default select-none items-center justify-center gap-1 rounded-md border',
        compact ? 'px-1.5 py-[1px] text-[9px]' : 'px-2 py-0.5 text-[10px]',
        'font-semibold leading-tight shadow-sm outline-none transition',
        tone,
        isDimmed && 'opacity-45'
      )}
      title={short}
    >
      <Icon className={cn('shrink-0 opacity-95', compact ? 'h-2.5 w-2.5' : 'h-3 w-3', iconTone)} aria-hidden />
      <span className="truncate">{short}</span>
    </div>
  );
}

function buildCommunitySlots(communityCards) {
  const slots = [null, null, null, null, null];
  (communityCards || []).forEach((c, i) => {
    if (i < 5) slots[i] = parseSekaCard(c);
  });
  return slots;
}

function dummyHoles(n = 3) {
  return Array.from({ length: n }, () => ({ suit: 'spades', rank: 'A' }));
}

function getBlindsLabel(table) {
  if (table.smallBlind != null && table.bigBlind != null) {
    return `${table.smallBlind}/${table.bigBlind} SEKA`;
  }
  if (table.stakes != null) return String(table.stakes);
  if (table.buyIn != null) return `Вход ${table.buyIn} SEKA`;
  return '—';
}

function readReducedMotionPreference() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Инициалы для аватарки-заглушки в голосовании «готов». */
function seatInitialsFromName(name) {
  const s = String(name || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
  return s.slice(0, 2).toUpperCase();
}

/** Аватар со стола: бот / URL / инициалы при ошибке или пустом URL. */
function OnlineSeatAvatarBubble({ isBot, displayName, avatarUrl, frameClassName, initialsClassName }) {
  const trimmed = String(avatarUrl || '').trim();
  const [imgBroken, setImgBroken] = useState(false);
  useEffect(() => {
    setImgBroken(false);
  }, [trimmed, isBot]);
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/20 bg-black/35',
        frameClassName
      )}
    >
      {isBot ? (
        <img src="/avatar-for-bot.png" alt="" className="h-full w-full object-cover" draggable={false} />
      ) : trimmed && !imgBroken ? (
        <img
          src={trimmed}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
          onError={() => setImgBroken(true)}
        />
      ) : (
        <span className={cn('font-black text-amber-50', initialsClassName || 'text-[10px]')}>
          {seatInitialsFromName(displayName)}
        </span>
      )}
    </span>
  );
}

function GameTableInner({
  tableId,
  gameRoomId = '',
  colyseusRoomName = 'seka',
  accessToken,
  user,
  onLeaveTable,
  onBack,
  onSendFriendRequest,
  onSessionOutcome,
  incomingInvite = null,
  onRespondInvite,
  returnView = 'online-lobby',
  onMatchAborted,
  onReconnectWait,
}) {
  const authUserId = String(user?.id || '').trim();
  const authUsername = String(user?.username || 'Игрок');
  const authAvatarUrl = String(user?.avatarUrl || '').trim();
  const authLevel = Math.max(1, Math.floor(Number(user?.level) || 1));
  const handleLeaveTable = onLeaveTable || onBack;
  const { play, playLoop, stopLoop, stopLoopBySound, initAudio } = useAudio();
  const gameTransportRef = useRef(null);
  /** Один раз при входе в waiting с ≥2 игроками — авто-старт (после «готов» в лобби). */
  const waitingAutoStartSentRef = useRef(false);
  /** Антиспам тостов «переподключение выполнено» при нестабильной сети */
  const reconnectToastAtRef = useRef(0);
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gameState, setGameState] = useState({
    schemaVersion: SEKA_SCHEMA_VERSION,
    handSeq: 0,
    turnSeatIndex: 0,
    currentPlayer: 0,
    communityCards: [],
    pot: 0,
    currentBet: 0,
    deck: [],
    deckRemaining: 0,
    turnDeadlineAtMs: null,
    turnBudgetMs: 30_000,
    handMoves: [],
    phase: 'preflop',
    playerCards: {},
    roundNumber: 0,
    holeEpoch: 0,
    completedHandSummaries: [],
    gameHistory: [],
    nextHandReadyUserIds: [],
    nextHandReadyDeadlineAtMs: null,
    nextHandReadyBudgetMs: null,
    graceExit: null,
    sessionOutcomeEvents: [],
  });

  const [notifications, setNotifications] = useState([]);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [gameChatMessages, setGameChatMessages] = useState([]);
  const [gameChatDraft, setGameChatDraft] = useState('');
  const [gameChatNextAtMs, setGameChatNextAtMs] = useState(0);
  const [flyingCoins, setFlyingCoins] = useState(null);
  const [showFlyingCoins, setShowFlyingCoins] = useState(false);
  const [antePhaseText, setAntePhaseText] = useState('');
  const [anteSeatBursts, setAnteSeatBursts] = useState([]);
  const [anteFlights, setAnteFlights] = useState([]);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(readReducedMotionPreference);
  const [svarkaNowMs, setSvarkaNowMs] = useState(Date.now());
  const [svarkaChoiceSent, setSvarkaChoiceSent] = useState(false);
  const wasMyTurn = useRef(false);
  const wasSvarkaCommitOpenRef = useRef(false);
  const anteQueueRef = useRef([]);
  const anteRunningRef = useRef(false);
  const anteTimerIdsRef = useRef([]);
  const anteFlightClearTimerRef = useRef(null);
  const handledAnteEventIdsRef = useRef(new Set());
  const handledAnteHandSeqRef = useRef(new Set());
  const matchAbortHandledRef = useRef(false);
  const removeTransportUiListenersRef = useRef(null);
  const matchAbortPendingPayloadRef = useRef(null);
  const [matchAbortOverlayOutcome, setMatchAbortOverlayOutcome] = useState(null);
  const handledMoveFxKeysRef = useRef(new Set());
  const prevMoveFxMetaRef = useRef({ handSeq: 0, movesLen: 0, pot: 0 });
  const heroTimerLoopRef = useRef(null);
  const svarkaLoopRef = useRef(null);
  /** One-shot + тост при авто-сваре (фаза svarkaBetween без модалки), как в LocalGame. */
  const lastSvarkaAutoBetweenFxKeyRef = useRef('');
  const lastOutcomeSoundHandSeqRef = useRef(-1);
  const prefersReducedMotionRef = useRef(prefersReducedMotion);
  const seatArenaRef = useRef(null);
  const seatDragRef = useRef(null);
  const seatOffsetsLayoutReloadKeyRef = useRef('');
  const onlineSeatBlockKey = useMemo(
    () => `${String(tableId)}:${String(gameRoomId || '')}`,
    [tableId, gameRoomId]
  );
  const [seatArrangeMode, setSeatArrangeMode] = useState(false);
  const [seatOffsetBySeat, setSeatOffsetBySeat] = useState({});
  const [seatProfileOpen, setSeatProfileOpen] = useState(null);
  const [showRaiseInput, setShowRaiseInput] = useState(false);
  const [sekaRaiseAmount, setSekaRaiseAmount] = useState(0);
  const [liveMovesOpen, setLiveMovesOpen] = useState(false);
  const [deckPulseKey, setDeckPulseKey] = useState(0);
  const [turnNowMs, setTurnNowMs] = useState(() => Date.now());
  /** Если сервер не прислал turnDeadlineAtMs — локальный отсчёт для кольца таймера в IndustrialSeat. */
  const [localTurnFallbackStart, setLocalTurnFallbackStart] = useState(null);
  const turnFallbackKeyRef = useRef('');
  const prevHandSeqFxRef = useRef(0);
  /** Накопление хроники по завершённым раздачам (клиент). */
  const [matchChronicleHands, setMatchChronicleHands] = useState([]);
  const lastHandSeqChronicleRef = useRef(0);
  const lastHandMovesSnapshotRef = useRef([]);
  const potAnchorRef = useRef(null);
  const heroSeatAnchorRef = useRef(null);
  /** Слоты карт нижней полосы героя (паритет с LocalGame для CardPair / ключей). */
  const heroStripSlotRefs = useRef([React.createRef(), React.createRef(), React.createRef()]);
  const deckFlightRef = useRef(null);
  const anteSourceRefsRef = useRef({});
  /** Слоты рубашек у места [seat][0..2] — цели полёта от колоды. */
  const seatCardSlotRefsRef = useRef({});
  const seatLayoutSlotsRef = useRef([]);
  const dealGenRef = useRef(0);
  const dealFlightAbortRef = useRef(false);
  const dealFlightRootsRef = useRef([]);
  const dealFlightAnimsRef = useRef([]);
  const emptyActionsWatchdogRef = useRef({ key: '', since: 0, fired: false });
  const peakPotRef = useRef(0);
  const lastFlyingAwardedHandSeqRef = useRef(-1);
  /** Инициатор нажал «Забрать выигрыш» — ждём broadcast, затем оверлей → выход. */
  const graceExitInitiatorPendingRef = useRef(false);
  const graceExitLeaveAfterOverlayRef = useRef(false);
  const tableRefForDealFx = useRef(null);
  const prevShowOnlineBuyInPanelRef = useRef(false);
  /** Побед героя за текущую сессию подключения к этому столу. */
  const [sessionHeroWins, setSessionHeroWins] = useState(0);
  const [sessionTotalHands, setSessionTotalHands] = useState(0);
  const [sessionHeroLosses, setSessionHeroLosses] = useState(0);
  const [showHandOutcomeModal, setShowHandOutcomeModal] = useState(false);
  const [sessionOutcomeOverlay, setSessionOutcomeOverlay] = useState(null);
  const [handOutcomeCopyFeedback, setHandOutcomeCopyFeedback] = useState(false);
  const [handOutcomeDetailsExpanded, setHandOutcomeDetailsExpanded] = useState(false);
  const lastHandOutcomeHandledSeqRef = useRef(-1);
  const handOutcomeCopyTimerRef = useRef(null);
  const [showMatchSessionModal, setShowMatchSessionModal] = useState(false);
  const [matchSessionChronicleHeroOnly, setMatchSessionChronicleHeroOnly] = useState(false);
  const [matchSessionFullCopyFeedback, setMatchSessionFullCopyFeedback] = useState(false);
  const matchSessionCopyTimerRef = useRef(null);
  const [onlineBuyInDraft, setOnlineBuyInDraft] = useState(100);
  const [showInviteDecisionPicker, setShowInviteDecisionPicker] = useState(false);
  const inviteBubbleRef = useRef(null);
  const handledOutcomeIdsRef = useRef(new Set());
  /** Сессионный оверлей: откладываем winner-game, чтобы не перекрыть money-SFX. */
  const sessionOutcomeOverlayRef = useRef(null);
  const winnerFanfareFallbackTimerRef = useRef(null);
  const winnerFanfarePlayAfterSessionOverlayCloseRef = useRef(false);
  const winnerFanfarePlayedForHandRef = useRef(false);

  const heroUserId = user?.id != null ? String(user.id) : '';

  const heroGraceChipNet = useMemo(() => {
    const ge = gameState?.graceExit;
    if (!ge?.paidInByUserId || !heroUserId) return null;
    const paidRaw = ge.paidInByUserId[heroUserId] ?? ge.paidInByUserId[String(heroUserId)];
    const paid = Math.max(0, Math.floor(Number(paidRaw) || 0));
    const tp = Array.isArray(table?.players)
      ? table.players.find((p) => p && String(p.userId) === String(heroUserId))
      : null;
    if (!tp) return null;
    const chips = Math.max(0, Math.floor(Number(tp.chips ?? tp.balance ?? 0)));
    const bet = Math.max(0, Math.floor(Number(tp.currentBet ?? tp.bet ?? 0)));
    return chips + bet - paid;
  }, [gameState?.graceExit, table?.players, heroUserId]);

  const completeMatchAbortDismiss = useCallback(() => {
    setMatchAbortOverlayOutcome(null);
    const payload = matchAbortPendingPayloadRef.current;
    matchAbortPendingPayloadRef.current = null;
    const afk =
      Boolean(payload?.afkMatchAbort) || String(payload?.reason || '') === 'player_afk';
    const name =
      typeof payload?.leaver?.username === 'string' && payload.leaver.username.trim()
        ? payload.leaver.username.trim()
        : 'Игрок';
    const nid = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setNotifications((prev) => [
      ...prev,
      {
        id: nid,
        message: afk
          ? 'Партия отменена: бездействие (много пропусков хода).'
          : `Партия отменена: ${name} вышел из матча.`,
        type: 'warning',
      },
    ]);
    removeTransportUiListenersRef.current?.();
    try {
      gameTransportRef.current?.disconnect?.();
    } catch {
      /* ignore */
    }
    onMatchAborted?.({ payload: payload || null, returnView });
  }, [returnView, onMatchAborted]);

  useEffect(() => {
    if (!incomingInvite?.id || !inviteBubbleRef.current) return undefined;
    const el = inviteBubbleRef.current;
    const enterTl = gsap.timeline();
    enterTl.fromTo(
      el,
      { x: 120, opacity: 0, scale: 0.92 },
      { x: 0, opacity: 1, scale: 1, duration: 0.32, ease: 'power3.out' }
    );
    enterTl.to(
      el,
      {
        boxShadow: '0 0 0 10px rgba(16,185,129,0)',
        repeat: 3,
        yoyo: true,
        duration: 0.42,
        ease: 'sine.inOut',
      },
      0.05
    );
    setShowInviteDecisionPicker(false);
    return () => {
      enterTl.kill();
    };
  }, [incomingInvite?.id]);

  const sendInviteDecisionFromGame = useCallback(
    (invite, decision) => {
      const inviteId = String(invite?.id || invite?.inviteId || '').trim();
      const normalizedDecision = String(decision || '').trim().toLowerCase();
      if (!inviteId || !normalizedDecision) return;
      gameTransportRef.current?.emit?.('social_resolve_invite', {
        inviteId,
        decision: normalizedDecision,
      });
      onRespondInvite?.(invite, normalizedDecision, { source: 'game' });
    },
    [onRespondInvite]
  );

  const currentPlayerIndex = useMemo(() => {
    if (!table?.players || !heroUserId) return -1;
    return table.players.findIndex((p) => p && String(p.userId) === heroUserId);
  }, [table, heroUserId]);

  const heroSeatSnapshot = useMemo(() => {
    if (!table?.players || currentPlayerIndex < 0) return null;
    const p = table.players[currentPlayerIndex];
    if (!p) return null;
    return {
      chips: Number(p.chips ?? 0),
      pendingBuyIn: Number(p.pendingBuyIn ?? 0),
      isSittingOut: Boolean(p.isSittingOut),
    };
  }, [table?.players, currentPlayerIndex]);

  const seatLayoutMeta = useMemo(() => {
    if (!table?.players?.length) {
      const arena = getSeatArenaTuning(2);
      return { slots: [], density: getSeatDensityPreset(2), arena };
    }
    const seats = table.players.map((p, i) => (p ? i : null)).filter((s) => s != null);
    const heroSeat = currentPlayerIndex >= 0 ? currentPlayerIndex : 0;
    const density = getSeatDensityPreset(seats.length);
    const arena = getSeatArenaTuning(seats.length);
    const slots = buildEllipticSeatSlots({
      seats,
      heroSeat,
      centerY: arena.centerY,
      radiusX: arena.radiusX,
      radiusY: arena.radiusY,
    });
    return { slots, density, arena };
  }, [table, currentPlayerIndex]);

  const isPlayingState = table?.state === 'playing';

  seatLayoutSlotsRef.current = seatLayoutMeta.slots;
  tableRefForDealFx.current = table;

  useEffect(() => {
    if (!table) return;
    const reloadKey = `${onlineSeatBlockKey}|${seatLayoutMeta.slots.length}`;
    if (seatOffsetsLayoutReloadKeyRef.current === reloadKey) return;
    seatOffsetsLayoutReloadKeyRef.current = reloadKey;
    setSeatOffsetBySeat(loadOffsetsForBlock(SEAT_OFFSET_STORAGE.online, onlineSeatBlockKey));
  }, [table, onlineSeatBlockKey, seatLayoutMeta.slots.length]);

  const handleSeatDragPointerDown = useCallback(
    (e, seat, slotLeft, slotTop) => {
      if (!seatArrangeMode) return;
      const arena = seatArenaRef.current;
      if (!arena) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = arena.getBoundingClientRect();
      const w = Math.max(1, rect.width);
      const h = Math.max(1, rect.height);
      const o = seatOffsetBySeat[seat] || { dLeft: 0, dTop: 0 };
      seatDragRef.current = {
        pointerId: e.pointerId,
        seat,
        slotLeft,
        slotTop,
        odl: o.dLeft,
        odt: o.dTop,
        sx: e.clientX,
        sy: e.clientY,
        aw: w,
        ah: h,
      };
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    [seatArrangeMode, seatOffsetBySeat]
  );

  const handleSeatDragPointerMove = useCallback((e) => {
    const d = seatDragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dxPct = ((e.clientX - d.sx) / d.aw) * 100;
    const dyPct = ((e.clientY - d.sy) / d.ah) * 100;
    let nl = d.odl + dxPct;
    let nt = d.odt + dyPct;
    const c = clampSeatCenter(d.slotLeft + nl, d.slotTop + nt);
    nl = c.leftPct - d.slotLeft;
    nt = c.topPct - d.slotTop;
    setSeatOffsetBySeat((prev) => {
      const cur = prev[d.seat];
      if (cur && cur.dLeft === nl && cur.dTop === nt) return prev;
      return { ...prev, [d.seat]: { dLeft: nl, dTop: nt } };
    });
  }, []);

  const handleSeatDragPointerUp = useCallback(
    (e) => {
      const d = seatDragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      seatDragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      setSeatOffsetBySeat((prev) => {
        saveOffsetsForBlock(SEAT_OFFSET_STORAGE.online, onlineSeatBlockKey, prev);
        return prev;
      });
    },
    [onlineSeatBlockKey]
  );

  const handleSeatDragLostCapture = useCallback(() => {
    const had = seatDragRef.current != null;
    seatDragRef.current = null;
    if (had) {
      setSeatOffsetBySeat((prev) => {
        saveOffsetsForBlock(SEAT_OFFSET_STORAGE.online, onlineSeatBlockKey, prev);
        return prev;
      });
    }
  }, [onlineSeatBlockKey]);

  const handleClearOnlineSeatOffsets = useCallback(() => {
    clearOffsetsForBlock(SEAT_OFFSET_STORAGE.online, onlineSeatBlockKey);
    setSeatOffsetBySeat({});
  }, [onlineSeatBlockKey]);

  useEffect(() => {
    prefersReducedMotionRef.current = prefersReducedMotion;
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setPrefersReducedMotion(Boolean(media.matches));
    apply();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', apply);
      return () => media.removeEventListener('change', apply);
    }
    media.addListener(apply);
    return () => media.removeListener(apply);
  }, []);

  useEffect(() => {
    if (!authUserId) {
      setLoading(false);
      setError('Нужна авторизация');
      return undefined;
    }
    let cancelled = false;
    let transport = null;
    /** @type {null | (() => void)} */
    let removeTransportUiListeners = null;
    matchAbortHandledRef.current = false;

    const run = async () => {
      try {
        transport = await ColyseusSekaGameTransport.join({
          tableId,
          gameRoomId,
          accessToken,
          user: {
            id: authUserId,
            username: authUsername,
            avatarUrl: authAvatarUrl,
            level: authLevel,
          },
        });
        if (cancelled) {
          transport.disconnect();
          return;
        }
        gameTransportRef.current = transport;
        setError('');

        transport.onSnapshot((payload) => {
          if (!payload?.table || String(payload.table.id) !== String(tableId)) return;
          if (process.env.NODE_ENV !== 'production' && !isSekaGameStateSnapshotV1(payload)) {
            console.warn('[GameTable] unexpected game/state shape', payload);
          }
          setTable(payload.table);
          const round = payload.game ?? payload.gameState;
          if (round) {
            setGameState((prev) => {
              const prevSeq = prev.handSeq ?? 0;
              const nextSeq = round.handSeq ?? 0;
              const prevHole = Number(prev.holeEpoch ?? 0);
              const nextHole = Number(round.holeEpoch ?? 0);
              const prevSchema = Number(prev.schemaVersion ?? SEKA_SCHEMA_VERSION);
              const nextSchema = Number(payload.schemaVersion ?? SEKA_SCHEMA_VERSION);
              const keepPrivate =
                nextSchema === prevSchema &&
                nextSeq === prevSeq &&
                prevSeq > 0 &&
                nextHole === prevHole;
              return {
                ...round,
                schemaVersion: nextSchema,
                playerCards: keepPrivate ? { ...(prev.playerCards || {}) } : {},
              };
            });
          }
          setLoading(false);
        });

        transport.onPrivateHole((payload) => {
          const uid = payload?.userId != null ? String(payload.userId) : '';
          const cards = payload?.cards;
          if (!uid || !Array.isArray(cards)) return;
          setGameState((prev) => ({
            ...prev,
            playerCards: { ...(prev.playerCards || {}), [uid]: cards },
          }));
        });

        const toastId = () => `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const pushToast = (message, type) => {
          if (cancelled) return;
          setNotifications((prev) => [...prev, { id: toastId(), message, type }]);
        };

        const onReconnected = (payload) => {
          if (cancelled) return;
          const now = Date.now();
          if (now - reconnectToastAtRef.current < 2800) return;
          reconnectToastAtRef.current = now;
          pushToast(
            reconnectSuccessUserMessage({ context: 'table', fallbackJoin: isReconnectFallbackJoin(payload) }),
            'success'
          );
        };

        const onResyncRequired = () => {
          if (cancelled) return;
          pushToast('Состояние обновлено с сервера', 'info');
          setGameState((prev) => ({ ...prev, playerCards: {} }));
        };

        const onTransportError = (payload) => {
          if (cancelled) return;
          if (graceExitInitiatorPendingRef.current) {
            graceExitInitiatorPendingRef.current = false;
            const code = String(payload?.code || '');
            if (code === 'ALREADY_APPLIED') {
              pushToast('Бонус за эту паузу уже распределён.', 'info');
              return;
            }
          }
          if (isGameTransportErrorNoise(payload)) return;
          const msg =
            typeof payload?.message === 'string' && payload.message.trim()
              ? payload.message.trim()
              : 'Ошибка игры';
          pushToast(msg, 'warning');
        };

        const finishMatchAbort = (payload) => {
          if (cancelled || matchAbortHandledRef.current) return;
          const raw = payload && typeof payload === 'object' ? payload : {};
          const byUser = raw.matchAbortOutcomesByUserId;
          const myId = String(heroUserId || '').trim();
          let mine =
            myId && byUser && typeof byUser === 'object' ? byUser[myId] ?? byUser[String(myId)] : null;
          if (!mine && myId && byUser && typeof byUser === 'object') {
            const hit = Object.keys(byUser).find((k) => String(k).trim() === myId);
            if (hit) mine = byUser[hit];
          }

          if (mine) {
            matchAbortHandledRef.current = true;
            matchAbortPendingPayloadRef.current = raw;
            setLoading(false);
            setMatchAbortOverlayOutcome(mine);
            onSessionOutcome?.(mine);
            return;
          }

          matchAbortHandledRef.current = true;
          const afk =
            Boolean(raw?.afkMatchAbort) || String(raw?.reason || '') === 'player_afk';
          pushToast(
            afk
              ? 'Партия отменена: бездействие (много пропусков хода).'
              : 'Партия отменена: игрок вышел из матча.',
            'warning'
          );
          setLoading(false);
          removeTransportUiListeners?.();
          try {
            transport?.disconnect();
          } catch {
            /* ignore */
          }
          onMatchAborted?.({ payload: raw || null, returnView });
        };

        const onDisconnect = (payload) => {
          if (cancelled) return;
          if (payload?.intentional) return;
          if (payload?.matchAborted) {
            finishMatchAbort(payload);
            return;
          }
          if (payload?.duplicateSession) {
            pushToast(duplicateSessionUserMessage('table'), 'warning');
            setError(duplicateSessionSetErrorMessage('table'));
            setLoading(false);
            setTimeout(() => {
              if (!cancelled) handleLeaveTable?.();
            }, 0);
            return;
          }
          pushToast(finalDisconnectUserMessage('table', payload?.error), 'error');
          setError(finalDisconnectInlineErrorText('table'));
          setLoading(false);
        };

        const clearAnteTimers = () => {
          anteTimerIdsRef.current.forEach((id) => clearTimeout(id));
          anteTimerIdsRef.current = [];
          if (anteFlightClearTimerRef.current) {
            clearTimeout(anteFlightClearTimerRef.current);
            anteFlightClearTimerRef.current = null;
          }
        };

        const finishAnteAnimation = () => {
          clearAnteTimers();
          setAntePhaseText('');
          setAnteSeatBursts([]);
          setAnteFlights([]);
          anteRunningRef.current = false;
        };

        const buildAnteFlights = (contributions) => {
          const potRect = potAnchorRef.current?.getBoundingClientRect?.();
          const deckRect = deckFlightRef.current?.getBoundingClientRect?.();
          const targetX = potRect
            ? potRect.left + potRect.width / 2
            : deckRect
              ? deckRect.left + deckRect.width / 2
              : window.innerWidth / 2;
          const targetY = potRect
            ? potRect.top + potRect.height / 2
            : deckRect
              ? deckRect.top + deckRect.height / 2
              : window.innerHeight / 2;
          return contributions.map((entry, idx) => {
            const seat = Number(entry.seatIndex);
            const seatRect = anteSourceRefsRef.current?.[seat]?.current?.getBoundingClientRect?.();
            const startX = seatRect ? seatRect.left + seatRect.width / 2 : targetX;
            const startY = seatRect ? seatRect.top + seatRect.height / 2 : targetY;
            const dx = targetX - startX;
            const dy = targetY - startY;
            const spreadX = Math.round((Math.random() - 0.5) * 34);
            const spreadY = Math.round((Math.random() - 0.5) * 22);
            return {
              id: `${entry.key}-${idx}`,
              amount: Number(entry.amount || 0),
              delayMs: Number(entry.delayMs || 0),
              startX,
              startY,
              dx,
              dy,
              coinDx: dx + spreadX,
              coinDy: dy + spreadY,
            };
          });
        };

        const runNextAnteEvent = () => {
          if (cancelled) return;
          if (anteRunningRef.current) return;
          const next = anteQueueRef.current.shift();
          if (!next) return;

          anteRunningRef.current = true;
          clearAnteTimers();
          setAnteSeatBursts([]);

          const phaseMs = Math.max(600, Math.min(900, Number(next.phaseMs || 760)));
          const staggerMs = Math.max(60, Math.min(90, Number(next.staggerMs || 75)));
          const contributions = [...(next.contributions || [])]
            .filter((c) => Number(c?.amount || 0) > 0 && Number(c?.seatIndex) >= 0)
            .sort((a, b) => Number(a.seatIndex) - Number(b.seatIndex))
            .map((c, idx) => ({
              seatIndex: Number(c.seatIndex),
              amount: Number(c.amount),
              delayMs: idx * staggerMs,
              key: `${next.eventId || next.handSeq || 'ante'}-${Number(c.seatIndex)}-${idx}`,
            }));

          const nominal = Number(next.ante || 0);
          const effStr = nominal.toLocaleString('ru-RU');
          const anyShortAnte =
            nominal > 0 &&
            contributions.some(
              (c) => Number(c.amount) > 0 && Number(c.amount) < nominal
            );
          setAntePhaseText(
            anyShortAnte
              ? `Взнос: номинал ${effStr} SEKA · авто-вскрытие (all-in анте)`
              : `Анте: ${effStr} SEKA с каждого`
          );

          if (prefersReducedMotionRef.current) {
            play('ante-coin', 0.92);
            setAnteFlights([]);
            const id = setTimeout(() => {
              finishAnteAnimation();
              runNextAnteEvent();
            }, phaseMs);
            anteTimerIdsRef.current.push(id);
            return;
          }

          const showBurstId = setTimeout(() => {
            play('ante-coin', 0.92);
            setAnteFlights(buildAnteFlights(contributions));
            setAnteSeatBursts(contributions);
          }, phaseMs);
          anteTimerIdsRef.current.push(showBurstId);

          const maxDelay = contributions.length > 0 ? contributions[contributions.length - 1].delayMs : 0;
          const clearId = setTimeout(() => {
            setAnteSeatBursts([]);
            setAntePhaseText('');
            setAnteFlights([]);
            anteRunningRef.current = false;
            runNextAnteEvent();
          }, phaseMs + maxDelay + 680);
          anteTimerIdsRef.current.push(clearId);
          anteFlightClearTimerRef.current = clearId;
        };

        const onAnteTaken = (payload) => {
          if (cancelled || !payload || String(payload.type || '') !== 'ANTE_TAKEN') return;
          if (payload.tableId != null && String(payload.tableId) !== String(tableId)) return;

          const eventId = typeof payload.eventId === 'string' ? payload.eventId : '';
          const handSeq = Number(payload.handSeq);
          if (eventId && handledAnteEventIdsRef.current.has(eventId)) return;
          if (Number.isFinite(handSeq) && handledAnteHandSeqRef.current.has(handSeq)) return;

          if (eventId) {
            handledAnteEventIdsRef.current.add(eventId);
            if (handledAnteEventIdsRef.current.size > 24) {
              const first = handledAnteEventIdsRef.current.values().next().value;
              if (first) handledAnteEventIdsRef.current.delete(first);
            }
          }
          if (Number.isFinite(handSeq)) {
            handledAnteHandSeqRef.current.add(handSeq);
            if (handledAnteHandSeqRef.current.size > 24) {
              const first = handledAnteHandSeqRef.current.values().next().value;
              if (first != null) handledAnteHandSeqRef.current.delete(first);
            }
          }

          anteQueueRef.current.push({
            eventId,
            handSeq,
            ante: Number(payload.ante || 0),
            contributions: Array.isArray(payload.contributions) ? payload.contributions : [],
            phaseMs: 760,
            staggerMs: 75,
          });
          runNextAnteEvent();
        };
        const onGameChatMessage = (payload) => {
          if (cancelled || !payload || typeof payload !== 'object') return;
          setGameChatMessages((prev) => [...prev.slice(-59), payload]);
        };

        const onMatchAbortedMsg = (payload) => {
          finishMatchAbort(payload && typeof payload === 'object' ? payload : {});
        };
        const onMatchWaitingReconnect = (payload) => {
          if (cancelled) return;
          const p = payload && typeof payload === 'object' ? payload : {};
          setError('Матч на паузе: ожидаем реконнект игрока.');
          onReconnectWait?.({ payload: p, returnView });
        };
        const onMatchReconnectResumed = (payload) => {
          if (cancelled) return;
          const p = payload && typeof payload === 'object' ? payload : {};
          setError('');
          pushToast('Игрок вернулся — продолжаем матч.', 'success');
          onReconnectWait?.({ payload: p, returnView });
        };
        const onMatchReconnectTimeout = (payload) => {
          if (cancelled) return;
          const p = payload && typeof payload === 'object' ? payload : {};
          setError('Игрок не вернулся вовремя. Завершаем матч...');
          onReconnectWait?.({ payload: p, returnView });
        };

        const onSekaGraceRespect = (payload) => {
          if (cancelled || !payload || typeof payload !== 'object') return;
          const outcomes = Array.isArray(payload.outcomes) ? payload.outcomes : [];
          const hid = String(heroUserId || '').trim();
          const heroOut = outcomes.find((o) => String(o?.userId || '').trim() === hid);
          if (heroOut) {
            const oid = String(heroOut.outcomeId || '').trim();
            if (oid && !handledOutcomeIdsRef.current.has(oid)) {
              handledOutcomeIdsRef.current.add(oid);
              setSessionOutcomeOverlay(heroOut);
              onSessionOutcome?.(heroOut);
            }
          }
          setShowHandOutcomeModal(false);
          setHandOutcomeDetailsExpanded(false);
          const initiator = String(payload.initiatorUserId || '').trim();
          if (initiator && initiator === hid && graceExitInitiatorPendingRef.current) {
            graceExitInitiatorPendingRef.current = false;
            graceExitLeaveAfterOverlayRef.current = true;
          } else if (initiator && initiator !== hid) {
            const code = String(tableId || '')
              .trim()
              .toUpperCase();
            pushToast(
              code
                ? `Соперник вышел с бонусом респекта. Вы остаётесь за столом — в онлайн-лобби по коду ${code} могут присоединиться новые игроки.`
                : 'Соперник вышел с бонусом респекта. Вы остаётесь за столом — ожидайте нового игрока из онлайн-лобби.',
              'info'
            );
          }
        };

        transport.on('reconnected', onReconnected);
        transport.on('resync_required', onResyncRequired);
        transport.on('error', onTransportError);
        transport.on('disconnect', onDisconnect);
        transport.on('ante_taken', onAnteTaken);
        transport.on('game_chat_message', onGameChatMessage);
        transport.on('match_aborted', onMatchAbortedMsg);
        transport.on('match_waiting_reconnect', onMatchWaitingReconnect);
        transport.on('match_reconnect_resumed', onMatchReconnectResumed);
        transport.on('match_reconnect_timeout', onMatchReconnectTimeout);
        transport.on('seka_grace_respect', onSekaGraceRespect);

        removeTransportUiListeners = () => {
          transport.off('reconnected', onReconnected);
          transport.off('resync_required', onResyncRequired);
          transport.off('error', onTransportError);
          transport.off('disconnect', onDisconnect);
          transport.off('ante_taken', onAnteTaken);
          transport.off('game_chat_message', onGameChatMessage);
          transport.off('match_aborted', onMatchAbortedMsg);
          transport.off('match_waiting_reconnect', onMatchWaitingReconnect);
          transport.off('match_reconnect_resumed', onMatchReconnectResumed);
          transport.off('match_reconnect_timeout', onMatchReconnectTimeout);
          transport.off('seka_grace_respect', onSekaGraceRespect);
          clearAnteTimers();
          anteQueueRef.current = [];
          anteRunningRef.current = false;
          setAntePhaseText('');
          setAnteSeatBursts([]);
          setAnteFlights([]);
        };
        removeTransportUiListenersRef.current = removeTransportUiListeners;
      } catch (err) {
        console.error('Colyseus seka room:', err);
        if (!cancelled) {
          setError('Не удалось подключиться к столу (Colyseus)');
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      gameTransportRef.current = null;
      removeTransportUiListenersRef.current = null;
      if (transport) {
        removeTransportUiListeners?.();
        transport.disconnect();
      }
    };
  }, [
    tableId,
    gameRoomId,
    colyseusRoomName,
    accessToken,
    authUserId,
    authUsername,
    authAvatarUrl,
    authLevel,
    play,
    returnView,
    handleLeaveTable,
    onMatchAborted,
    onReconnectWait,
    onSessionOutcome,
    heroUserId,
  ]);

  const handlePlayerMove = useCallback(
    (action, amount = 0) => {
      gameTransportRef.current?.emit('player_move', {
        tableId,
        action,
        amount: Number(amount) || 0,
      });
      if (String(action || '').toUpperCase() === 'RAISE') {
        setShowRaiseInput(false);
      }
    },
    [tableId]
  );

  const handleSvarkaChoice = (participate) => {
    play(participate ? 'svarka-x8-begin' : 'svarka-x8-cancel', 1);
    gameTransportRef.current?.emit('svarka_choice', {
      tableId,
      participate: Boolean(participate),
    });
    setSvarkaChoiceSent(true);
  };

  const handleSendGameChat = useCallback(() => {
    const text = String(gameChatDraft || '').trim();
    if (!text) return;
    const now = Date.now();
    if (now < gameChatNextAtMs) return;
    gameTransportRef.current?.emit('game_chat_send', { text });
    setGameChatDraft('');
    setGameChatNextAtMs(now + 5000);
  }, [gameChatDraft, gameChatNextAtMs]);

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const getAvailableActions = useCallback(() => {
    if (!table || !gameState) return [];

    const turnSeatIndex = gameState.turnSeatIndex ?? gameState.currentPlayer ?? 0;
    const currentPlayer = table.players[turnSeatIndex];
    if (!currentPlayer || String(currentPlayer.userId) !== heroUserId) return [];

    const serverList = gameState.allowedActions;
    if (Array.isArray(serverList)) {
      const mapped = serverList.map((a) => {
        const action = String(a?.action || '').toUpperCase();
        return {
          action,
          label: typeof a?.label === 'string' && a.label ? a.label : action,
          color: SERVER_ACTION_COLORS[action] || 'gray',
        };
      });
      if (currentPlayer.isDark && mapped.some((x) => x.action === 'LOOK')) {
        const first = mapped.filter((x) => x.action === 'LOOK');
        const rest = mapped.filter((x) => x.action !== 'LOOK');
        return [...first, ...rest];
      }
      return mapped;
    }
    return [];
  }, [table, gameState, heroUserId]);

  useEffect(() => {
    waitingAutoStartSentRef.current = false;
  }, [tableId, gameRoomId]);

  useEffect(() => {
    if (!table || table.state !== 'waiting') return;
    const n = Array.isArray(table.players) ? table.players.filter(Boolean).length : 0;
    if (n < 2) return;
    if (waitingAutoStartSentRef.current) return;
    waitingAutoStartSentRef.current = true;
    gameTransportRef.current?.emit('start_game', { tableId });
  }, [table, tableId]);

  const turnSeatIndex = gameState.turnSeatIndex ?? gameState.currentPlayer ?? 0;
  const turnTimerKey = useMemo(
    () => `${gameState.handSeq ?? 0}:${turnSeatIndex}:${currentPlayerIndex}`,
    [gameState.handSeq, turnSeatIndex, currentPlayerIndex]
  );
  const svarkaCommit = gameState?.svarkaCommit && gameState.svarkaCommit.active ? gameState.svarkaCommit : null;
  const isSvarkaCommitOpen = Boolean(table?.state === 'playing' && svarkaCommit);
  const mySvarkaSeat = svarkaCommit?.seats?.find(
    (seat) => String(seat.userId) === heroUserId || seat.seatIndex === currentPlayerIndex
  );
  const svarkaMultOnline = Math.max(1, Number(gameState?.svarkaAnteMultiplier || 1));
  const heroCanChooseSvarkaOnline = Boolean(
    svarkaCommit?.phase === 'collecting' &&
      mySvarkaSeat?.canParticipate &&
      !heroSeatSnapshot?.isSittingOut
  );
  const svarkaRemainingMs =
    svarkaCommit && svarkaCommit.phase === 'collecting'
      ? Math.max(0, Number(svarkaCommit.deadlineAtMs || 0) - svarkaNowMs)
      : 0;
  const svarkaCountdownRatioOnline = Math.max(
    0,
    Math.min(1, svarkaRemainingMs / ONLINE_SVARKA_COMMIT_TOTAL_MS)
  );
  const svarkaCountdownWidthOnline = `${Math.round(svarkaCountdownRatioOnline * 1000) / 10}%`;
  const svarkaCountdownShouldAnimateWidthOnline =
    svarkaCommit?.phase === 'collecting' && svarkaRemainingMs < ONLINE_SVARKA_COMMIT_TOTAL_MS;
  const svarkaCommitSecondsLeft = Math.max(0, Math.ceil(svarkaRemainingMs / 1000));
  const svarkaCountdownLineClassOnline =
    svarkaCommitSecondsLeft <= 1
      ? 'from-red-400 via-red-500 to-red-600 shadow-[0_0_16px_rgba(248,113,113,0.6)]'
      : svarkaCommitSecondsLeft <= 2
        ? 'from-orange-300 via-orange-400 to-red-500 shadow-[0_0_14px_rgba(251,146,60,0.55)]'
        : svarkaCommitSecondsLeft <= 3
          ? 'from-amber-300 via-amber-400 to-orange-500 shadow-[0_0_12px_rgba(251,191,36,0.5)]'
          : 'from-emerald-300 via-emerald-400 to-amber-300 shadow-[0_0_12px_rgba(52,211,153,0.45)]';

  /** Как `SekaRoom._executeSekaMatchAbort`: chips + currentBet — точное списание с места. */
  const leaveModalHeroMoney = useMemo(() => {
    if (!table?.players?.length) return { chips: 0, streetBet: 0, exposure: 0 };
    const idx = table.players.findIndex((p) => p && String(p.userId) === heroUserId);
    if (idx < 0) return { chips: 0, streetBet: 0, exposure: 0 };
    const h = table.players[idx];
    const chips = Math.max(0, Math.floor(Number(h?.chips ?? h?.balance ?? 0)));
    const streetBet = Math.max(0, Math.floor(Number(h?.currentBet ?? h?.bet ?? 0)));
    return { chips, streetBet, exposure: chips + streetBet };
  }, [table, heroUserId]);
  const leaveModalHeroExposure = leaveModalHeroMoney.exposure;
  const leaveModalHeroChips = leaveModalHeroMoney.chips;
  const leaveModalHeroStreetBet = leaveModalHeroMoney.streetBet;

  const leaveModalBuyIn = useMemo(() => {
    const tableBi = Number(table?.buyIn);
    return Math.max(
      1,
      Number.isFinite(tableBi) && tableBi > 0 ? Math.floor(tableBi) : DEFAULT_SEKA_CONFIG.startingChips
    );
  }, [table?.buyIn]);

  const handleHeaderLeave = useCallback(() => {
    if (!table) {
      handleLeaveTable();
      return;
    }
    if (table.state === 'waiting' && !(Number(gameState?.handSeq) > 0)) {
      handleLeaveTable();
      return;
    }
    if (isSvarkaCommitOpen) {
      setNotifications((prev) => [
        ...prev,
        {
          id: `leave-svarka-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          message: 'Закончите выбор по сварке — затем можно покинуть стол.',
          type: 'info',
        },
      ]);
      return;
    }
    if (table.state === 'playing' && String(gameState?.phase || '') === 'svarkaBetween') {
      setNotifications((prev) => [
        ...prev,
        {
          id: `leave-svarka-phase-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          message: 'Подождите окончания этапа свара или следующей раздачи.',
          type: 'info',
        },
      ]);
      return;
    }
    setLeaveModalOpen(true);
  }, [
    table,
    gameState?.handSeq,
    gameState?.phase,
    isSvarkaCommitOpen,
    handleLeaveTable,
  ]);

  const handleConfirmLeaveOnline = useCallback(() => {
    setLeaveModalOpen(false);
    /** Не вызываем handleLeaveTable сразу: иначе размонтирование снимает слушатели до MATCH_ABORTED — крыса не видит SessionResultOverlay. Размонтаж даст `completeMatchAbortDismiss` → onMatchAborted. */
    try {
      gameTransportRef.current?.disconnect?.();
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!table) setLeaveModalOpen(false);
  }, [table]);

  const isCurrentPlayerTurn = turnSeatIndex === currentPlayerIndex;
  const serverAllowedActions = Array.isArray(gameState?.allowedActions) ? gameState.allowedActions : [];
  const canAct =
    table?.state === 'playing' &&
    !gameState?.gameEnded &&
    isCurrentPlayerTurn &&
    !isSvarkaCommitOpen &&
    serverAllowedActions.length > 0;

  useEffect(() => {
    const watchdog = emptyActionsWatchdogRef.current;
    const shouldWatch =
      table?.state === 'playing' &&
      !gameState?.gameEnded &&
      isCurrentPlayerTurn &&
      !isSvarkaCommitOpen &&
      serverAllowedActions.length === 0;
    if (!shouldWatch) {
      watchdog.key = '';
      watchdog.since = 0;
      watchdog.fired = false;
      return;
    }
    const key = `${gameState?.handSeq ?? 0}:${turnSeatIndex}:${currentPlayerIndex}`;
    const now = Date.now();
    if (watchdog.key !== key) {
      watchdog.key = key;
      watchdog.since = now;
      watchdog.fired = false;
      return;
    }
    if (!watchdog.fired && now - watchdog.since >= 4500) {
      watchdog.fired = true;
      gameTransportRef.current?.emit('REQUEST_RESYNC', { reason: 'empty-actions-watchdog' });
      const id = `resync-watchdog-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setNotifications((prev) => [
        ...prev,
        { id, message: 'Синхронизируем ход с сервером...', type: 'info' },
      ]);
    }
  }, [
    table?.state,
    gameState?.gameEnded,
    gameState?.handSeq,
    isCurrentPlayerTurn,
    isSvarkaCommitOpen,
    serverAllowedActions.length,
    turnSeatIndex,
    currentPlayerIndex,
  ]);

  const showOnlineBuyInPanel = useMemo(() => {
    if (!table || table.state !== 'playing') return false;
    if (!gameState || gameState.gameEnded) return false;
    if (isSvarkaCommitOpen) return false;
    if (currentPlayerIndex < 0 || !heroSeatSnapshot) return false;
    const { chips, pendingBuyIn } = heroSeatSnapshot;
    return chips + pendingBuyIn <= 0;
  }, [table, gameState, isSvarkaCommitOpen, currentPlayerIndex, heroSeatSnapshot]);

  const userBalance = Math.max(0, Number(user?.balance ?? 0));
  const canSubmitOnlineBuyIn =
    showOnlineBuyInPanel && userBalance > 0;

  const clampedOnlineBuyIn = useMemo(() => {
    const max = Math.max(1, Math.floor(userBalance));
    const raw = Math.floor(Number(onlineBuyInDraft) || 0);
    return Math.min(max, Math.max(1, raw));
  }, [onlineBuyInDraft, userBalance]);

  /** Сумма стека героя за столом + очередь докупки (для подписей «наблюдатель»). */
  const heroStackWithPending = useMemo(() => {
    if (!heroSeatSnapshot) return 0;
    return Math.max(0, Number(heroSeatSnapshot.chips || 0)) + Math.max(0, Number(heroSeatSnapshot.pendingBuyIn || 0));
  }, [heroSeatSnapshot]);

  /**
   * Текст внизу, когда не ваш ход: ход соперника / вне раздачи / докупка в очереди.
   * Не используется во время сварки (полноэкранный оверлей).
   */
  const onlineIdleFooterCopy = useMemo(() => {
    if (!table) return null;
    if (isSvarkaCommitOpen) {
      return { title: 'Свара', detail: 'Примите решение в полноэкранном окне.' };
    }
    if (table.state === 'finished') {
      return {
        title: 'Раздача завершена',
        detail:
          'Нажмите «Готов к следующей раздаче» в окне итога (или внизу экрана). Новая рука начнётся, когда все игроки за столом проголосуют.',
      };
    }
    if (table.state !== 'playing') return null;
    if (gameState.gameEnded) {
      return { title: 'Итог раздачи', detail: 'Скоро продолжение матча.' };
    }
    if (showOnlineBuyInPanel) return null;
    if (currentPlayerIndex < 0 || !heroSeatSnapshot) {
      const p = table.players[turnSeatIndex];
      const nm =
        p?.username && String(p.username).trim()
          ? String(p.username).length > 18
            ? `${String(p.username).slice(0, 17)}…`
            : String(p.username)
          : 'Соперник';
      return { title: `Ходит ${nm}`, detail: null };
    }
    if (heroSeatSnapshot.pendingBuyIn > 0) {
      return {
        title: 'Докупка в очереди',
        detail: 'Стек пополнится в начале следующей раздачи. Пока вы наблюдаете за столом.',
      };
    }
    if (heroSeatSnapshot.isSittingOut && heroStackWithPending > 0) {
      return {
        title: 'Вы вне этой раздачи',
        detail: 'Следующая раздача — снова в игре, если хватит фишек на анте.',
      };
    }
    const actor = table.players[turnSeatIndex];
    if (actor && String(actor.userId) === heroUserId) {
      return { title: 'Ваш ход', detail: null };
    }
    const nm =
      actor?.username && String(actor.username).trim()
        ? String(actor.username).length > 18
          ? `${String(actor.username).slice(0, 17)}…`
          : String(actor.username)
        : 'Соперник';
    return { title: `Ходит ${nm}`, detail: 'Ожидайте…' };
  }, [
    table,
    isSvarkaCommitOpen,
    gameState.gameEnded,
    showOnlineBuyInPanel,
    currentPlayerIndex,
    heroSeatSnapshot,
    heroStackWithPending,
    turnSeatIndex,
    heroUserId,
  ]);

  useEffect(() => {
    if (!showOnlineBuyInPanel) {
      prevShowOnlineBuyInPanelRef.current = false;
      return;
    }
    const justOpened = !prevShowOnlineBuyInPanelRef.current;
    prevShowOnlineBuyInPanelRef.current = true;
    if (!justOpened) return;
    const tableBi = Number(table?.buyIn);
    const suggested = Math.min(
      userBalance,
      Math.max(1, Number.isFinite(tableBi) && tableBi > 0 ? Math.floor(tableBi) : DEFAULT_SEKA_CONFIG.startingChips)
    );
    setOnlineBuyInDraft(Math.max(1, Math.floor(suggested)));
  }, [showOnlineBuyInPanel, table?.buyIn, userBalance]);

  useEffect(() => {
    setSessionHeroWins(0);
    setSessionTotalHands(0);
    setSessionHeroLosses(0);
    lastHandOutcomeHandledSeqRef.current = -1;
    lastSvarkaAutoBetweenFxKeyRef.current = '';
    handledOutcomeIdsRef.current.clear();
    if (winnerFanfareFallbackTimerRef.current) {
      clearTimeout(winnerFanfareFallbackTimerRef.current);
      winnerFanfareFallbackTimerRef.current = null;
    }
    winnerFanfarePlayAfterSessionOverlayCloseRef.current = false;
    winnerFanfarePlayedForHandRef.current = false;
    setSessionOutcomeOverlay(null);
    setMatchAbortOverlayOutcome(null);
    matchAbortPendingPayloadRef.current = null;
    matchAbortHandledRef.current = false;
  }, [tableId, gameRoomId]);

  useEffect(
    () => () => {
      if (handOutcomeCopyTimerRef.current) {
        clearTimeout(handOutcomeCopyTimerRef.current);
        handOutcomeCopyTimerRef.current = null;
      }
      if (matchSessionCopyTimerRef.current) {
        clearTimeout(matchSessionCopyTimerRef.current);
        matchSessionCopyTimerRef.current = null;
      }
      if (heroTimerLoopRef.current) {
        stopLoop(heroTimerLoopRef.current);
        heroTimerLoopRef.current = null;
      }
      if (svarkaLoopRef.current) {
        stopLoop(svarkaLoopRef.current);
        svarkaLoopRef.current = null;
      }
      stopLoopBySound('timer-start');
      stopLoopBySound('svarka-x8');
      stopLoopBySound('svarka-x4');
      stopLoopBySound('svarka-x2');
    },
    [stopLoop, stopLoopBySound]
  );

  useEffect(() => {
    setShowMatchSessionModal(false);
    setMatchSessionChronicleHeroOnly(false);
  }, [tableId, gameRoomId]);

  useEffect(() => {
    if (!showHandOutcomeModal) setHandOutcomeDetailsExpanded(false);
  }, [showHandOutcomeModal]);

  useEffect(() => {
    sessionOutcomeOverlayRef.current = sessionOutcomeOverlay || matchAbortOverlayOutcome;
  }, [sessionOutcomeOverlay, matchAbortOverlayOutcome]);

  useEffect(() => {
    const o = sessionOutcomeOverlay || matchAbortOverlayOutcome;
    if (!o) return;
    if (String(o?.outcomeId || '').startsWith('match_abort:')) return;
    if (String(o?.outcomeId || '').startsWith('seka_grace_pnl:')) return;
    if (winnerFanfareFallbackTimerRef.current) {
      clearTimeout(winnerFanfareFallbackTimerRef.current);
      winnerFanfareFallbackTimerRef.current = null;
    }
    if (String(o?.result || '') === 'win' && !winnerFanfarePlayedForHandRef.current) {
      winnerFanfarePlayAfterSessionOverlayCloseRef.current = true;
    }
  }, [sessionOutcomeOverlay, matchAbortOverlayOutcome]);

  useEffect(() => {
    const rows = Array.isArray(gameState.sessionOutcomeEvents) ? gameState.sessionOutcomeEvents : [];
    if (!rows.length || !heroUserId) return;
    const mine = rows.filter((x) => String(x?.userId || '') === String(heroUserId));
    if (!mine.length) return;
    const latest = mine[mine.length - 1];
    const outcomeId = String(latest?.outcomeId || '').trim();
    if (!outcomeId || handledOutcomeIdsRef.current.has(outcomeId)) return;
    handledOutcomeIdsRef.current.add(outcomeId);
    setSessionOutcomeOverlay(latest);
    onSessionOutcome?.(latest);
  }, [gameState.sessionOutcomeEvents, heroUserId, onSessionOutcome]);

  const handleOnlineBuyIn = useCallback(() => {
    if (!canSubmitOnlineBuyIn) return;
    const clamped = clampedOnlineBuyIn;
    if (!Number.isFinite(clamped) || clamped <= 0) return;
    gameTransportRef.current?.emit('BUY_IN', { amount: clamped });
  }, [canSubmitOnlineBuyIn, clampedOnlineBuyIn]);

  useEffect(() => {
    if (isSvarkaCommitOpen && !wasSvarkaCommitOpenRef.current) {
      setSvarkaChoiceSent(false);
    }
    wasSvarkaCommitOpenRef.current = isSvarkaCommitOpen;
  }, [isSvarkaCommitOpen]);

  useEffect(() => {
    if (!isSvarkaCommitOpen || !svarkaCommit?.deadlineAtMs || svarkaCommit.phase !== 'collecting') return undefined;
    const id = setInterval(() => setSvarkaNowMs(Date.now()), 250);
    return () => clearInterval(id);
  }, [isSvarkaCommitOpen, svarkaCommit?.deadlineAtMs, svarkaCommit?.phase]);

  useEffect(() => {
    if (canAct && !wasMyTurn.current) {
      play('your-turn');
    }
    wasMyTurn.current = canAct;
  }, [canAct, play]);

  useEffect(() => {
    if (canAct) {
      if (!heroTimerLoopRef.current) {
        heroTimerLoopRef.current = playLoop('timer-start', 1);
      }
      return;
    }
    if (heroTimerLoopRef.current) {
      stopLoop(heroTimerLoopRef.current);
      heroTimerLoopRef.current = null;
    }
  }, [canAct, playLoop, stopLoop]);

  /** Фон модалки участия в сваре: только `svarka-x8` (1:1 с LocalGame). */
  useEffect(() => {
    if (!isSvarkaCommitOpen) {
      if (svarkaLoopRef.current) {
        stopLoop(svarkaLoopRef.current);
        svarkaLoopRef.current = null;
      }
      stopLoopBySound('svarka-x8');
      return;
    }
    if (!svarkaLoopRef.current) {
      const loopId = playLoop('svarka-x8', 0.95);
      if (loopId) {
        svarkaLoopRef.current = loopId;
      } else {
        play('svarka-x8', 0.98);
      }
    }
    return () => {
      if (svarkaLoopRef.current) {
        stopLoop(svarkaLoopRef.current);
        svarkaLoopRef.current = null;
      }
      stopLoopBySound('svarka-x8');
    };
  }, [isSvarkaCommitOpen, play, playLoop, stopLoop, stopLoopBySound]);

  /** Авто-свара без модалки: one-shot ×2/×4/×8 + тост, как в LocalGame. */
  useEffect(() => {
    if (!table || table.state !== 'playing') return;
    if (isSvarkaCommitOpen) return;
    const phase = String(gameState?.phase || '');
    if (phase !== 'svarkaBetween') return;
    const mult = Math.max(1, Number(gameState?.svarkaAnteMultiplier || 1));
    if (mult !== 2 && mult !== 4 && mult !== 8) return;
    const key = `${Number(gameState?.handSeq ?? 0)}-svarka-auto-${mult}`;
    if (lastSvarkaAutoBetweenFxKeyRef.current === key) return;
    lastSvarkaAutoBetweenFxKeyRef.current = key;
    const svarkaInitSound = mult === 8 ? 'svarka-x8-init' : mult === 4 ? 'svarka-x4' : 'svarka-x2';
    play(svarkaInitSound, 1);
    const nid = `svarka-auto-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setNotifications((prev) => [
      ...prev,
      {
        id: nid,
        message: `Пустой круг — свара. Следующее анте ×${mult}.`,
        type: 'info',
        kind: 'svarka_auto',
      },
    ]);
  }, [
    table,
    isSvarkaCommitOpen,
    gameState?.phase,
    gameState?.handSeq,
    gameState?.svarkaAnteMultiplier,
    play,
  ]);

  const handMoves = useMemo(
    () => (Array.isArray(gameState.handMoves) ? gameState.handMoves : []),
    [gameState.handMoves]
  );

  useEffect(() => {
    const hs = Number(gameState.handSeq || 0);
    const potNow = Number(gameState.pot || 0);
    const moves = Array.isArray(handMoves) ? handMoves : [];
    const prev = prevMoveFxMetaRef.current;
    if (Number(prev.handSeq || 0) === 0 && hs > 0 && moves.length > 0) {
      prevMoveFxMetaRef.current = { handSeq: hs, movesLen: moves.length, pot: potNow };
      return;
    }
    const isFreshHand = hs !== Number(prev.handSeq || 0);
    if (isFreshHand) {
      handledMoveFxKeysRef.current.clear();
      prevMoveFxMetaRef.current = { handSeq: hs, movesLen: 0, pot: potNow };
    }
    const fromIdx = Math.max(0, Number(prevMoveFxMetaRef.current.movesLen || 0));
    const newMoves = moves.slice(fromIdx);
    newMoves.forEach((m, j) => {
      const seat = Number(m?.seatIndex);
      const action = String(m?.action || '').toUpperCase();
      const ts = Number(m?.timestampMs || m?.timestamp || 0);
      const seq = m?.moveSeq;
      const key =
        seq != null && Number.isFinite(Number(seq))
          ? `${hs}:seq:${Number(seq)}`
          : `${hs}:${seat}:${action}:${ts}:${fromIdx + j}`;
      if (handledMoveFxKeysRef.current.has(key)) return;
      handledMoveFxKeysRef.current.add(key);
      switch (action) {
        case 'CHECK':
          play('check');
          break;
        case 'CALL':
          play('call');
          break;
        case 'RAISE':
          play('raise');
          break;
        case 'FOLD':
          play('fold');
          break;
        case 'SHOWDOWN':
        case 'AUTO_SHOWDOWN_ALLIN_ANTE':
          play('showdown');
          break;
        case 'LOOK':
          play('card-flip');
          break;
        default:
          break;
      }
      if (action === 'CALL' || action === 'RAISE') {
        const paid = Number(m?.paid || 0);
        if (paid > 0 || potNow > Number(prev.pot || 0)) {
          play('allin-push-chips', 0.9);
        }
      }
    });
    prevMoveFxMetaRef.current = { handSeq: hs, movesLen: moves.length, pot: potNow };
    if (handledMoveFxKeysRef.current.size > 200) {
      const next = new Set(Array.from(handledMoveFxKeysRef.current).slice(-120));
      handledMoveFxKeysRef.current = next;
    }
  }, [handMoves, gameState.handSeq, gameState.pot, play]);

  const turnBudgetMs = Math.max(5000, Number(gameState.turnBudgetMs) || 30_000);
  const turnDeadlineAtMs = gameState.turnDeadlineAtMs;

  const effectiveTableMinRaise = useMemo(() => {
    const mr = Number(gameState.minRaise);
    const ant = Number(gameState.ante);
    if (Number.isFinite(mr) && mr > 0) return mr;
    if (Number.isFinite(ant) && ant > 0) return ant;
    return DEFAULT_SEKA_CONFIG.minRaise;
  }, [gameState.minRaise, gameState.ante]);

  const tableRulesHint = useMemo(() => {
    const ante = Number(gameState.ante);
    if (!Number.isFinite(ante) || ante <= 0) return null;
    const mr = effectiveTableMinRaise;
    const sec = Math.max(1, Math.round(turnBudgetMs / 1000));
    return `Анте ${ante.toLocaleString('ru-RU')} SEKA · мин. шаг рейза +${mr.toLocaleString('ru-RU')} · ход до ${sec} с (как на сервере при SEKA_TURN_BUDGET_MS)`;
  }, [gameState.ante, effectiveTableMinRaise, turnBudgetMs]);

  useEffect(() => {
    const myTurn =
      table?.state === 'playing' &&
      !gameState.gameEnded &&
      !isSvarkaCommitOpen &&
      turnSeatIndex === currentPlayerIndex &&
      currentPlayerIndex >= 0;
    if (!myTurn || turnDeadlineAtMs != null) {
      setLocalTurnFallbackStart(null);
      turnFallbackKeyRef.current = '';
      return;
    }
    setLocalTurnFallbackStart((prev) => {
      if (turnFallbackKeyRef.current !== turnTimerKey) {
        turnFallbackKeyRef.current = turnTimerKey;
        return Date.now();
      }
      return prev ?? Date.now();
    });
  }, [
    turnTimerKey,
    turnDeadlineAtMs,
    table?.state,
    gameState.gameEnded,
    isSvarkaCommitOpen,
    turnSeatIndex,
    currentPlayerIndex,
  ]);

  useEffect(() => {
    if (isSvarkaCommitOpen) return undefined;
    const needTick =
      turnDeadlineAtMs != null ||
      (localTurnFallbackStart != null &&
        turnSeatIndex === currentPlayerIndex &&
        currentPlayerIndex >= 0);
    if (!needTick) return undefined;
    const id = setInterval(() => setTurnNowMs(Date.now()), 250);
    return () => clearInterval(id);
  }, [turnDeadlineAtMs, isSvarkaCommitOpen, localTurnFallbackStart, turnSeatIndex, currentPlayerIndex]);

  const turnTimerForSeat = useCallback(
    (seat) => {
      if (seat !== turnSeatIndex) {
        return { left: null, total: null, frac: undefined };
      }
      if (turnDeadlineAtMs != null) {
        const leftMs = Math.max(0, Number(turnDeadlineAtMs) - turnNowMs);
        const leftSec = Math.max(0, Math.ceil(leftMs / 1000));
        const totalSec = Math.max(1, Math.ceil(turnBudgetMs / 1000));
        const frac = Math.min(1, Math.max(0, leftMs / turnBudgetMs));
        return { left: leftSec, total: totalSec, frac };
      }
      if (localTurnFallbackStart != null) {
        const elapsed = turnNowMs - localTurnFallbackStart;
        const leftMs = Math.max(0, turnBudgetMs - elapsed);
        const leftSec = Math.max(0, Math.ceil(leftMs / 1000));
        const totalSec = Math.max(1, Math.ceil(turnBudgetMs / 1000));
        const frac = Math.min(1, Math.max(0, leftMs / turnBudgetMs));
        return { left: leftSec, total: totalSec, frac };
      }
      return { left: null, total: null, frac: undefined };
    },
    [turnSeatIndex, turnDeadlineAtMs, turnNowMs, turnBudgetMs, localTurnFallbackStart]
  );

  const raiseRange = useMemo(() => {
    if (!canAct || !showRaiseInput || !table) return null;
    const p = table.players[turnSeatIndex];
    if (!p) return null;
    const step = Math.max(1, effectiveTableMinRaise);
    const cb = Number(gameState.currentBet) || 0;
    const minR = cb + step;
    const chips = Number(p.chips || 0);
    const maxR = Math.min(chips * (p.isDark ? 2 : 1), cb + chips);
    if (minR > maxR) return null;
    return { min: minR, max: maxR };
  }, [canAct, showRaiseInput, table, turnSeatIndex, gameState.currentBet, effectiveTableMinRaise]);

  const clampedSekaRaise = useMemo(() => {
    if (!raiseRange) return null;
    return Math.min(raiseRange.max, Math.max(raiseRange.min, sekaRaiseAmount));
  }, [raiseRange, sekaRaiseAmount]);

  const lastSeatActions = useMemo(() => {
    const seatsCount = Math.max(1, Number(table?.players?.length) || 0);
    const rows = Array.from({ length: seatsCount }, () => null);
    const actionKindMap = {
      FOLD: 'fold',
      LOOK: 'look',
      CALL: 'call',
      CHECK: 'check',
      RAISE: 'raise',
      SHOWDOWN: 'showdown',
      AUTO_SHOWDOWN_ALLIN_ANTE: 'showdown',
    };
    handMoves.forEach((m) => {
      const seat = Number(m?.seatIndex);
      if (!Number.isInteger(seat) || seat < 0 || seat >= rows.length) return;
      const action = String(m?.action || '').toUpperCase();
      const amountRaw = m?.amount ?? m?.paid ?? m?.targetBet ?? null;
      const amount = Number(amountRaw);
      rows[seat] = {
        kind: actionKindMap[action] || action.toLowerCase(),
        amount: Number.isFinite(amount) ? amount : undefined,
        at: Number(m?.timestampMs || m?.timestamp || Date.now()),
      };
    });
    return rows;
  }, [handMoves, table?.players?.length]);
  const serverCompletedHandSummaries = useMemo(
    () => (Array.isArray(gameState.completedHandSummaries) ? gameState.completedHandSummaries : []),
    [gameState.completedHandSummaries]
  );
  const serverGameHistory = useMemo(
    () => (Array.isArray(gameState.gameHistory) ? gameState.gameHistory : []),
    [gameState.gameHistory]
  );
  const serverCompletedHandSummariesFiltered = useMemo(() => {
    if (!matchSessionChronicleHeroOnly || !heroUserId) return serverCompletedHandSummaries;
    return serverCompletedHandSummaries.filter((h) => serverHandSummaryInvolvesHero(h, heroUserId));
  }, [serverCompletedHandSummaries, matchSessionChronicleHeroOnly, heroUserId]);
  const latestHistoryEntry = useMemo(
    () => (serverGameHistory.length > 0 ? serverGameHistory[serverGameHistory.length - 1] : null),
    [serverGameHistory]
  );
  const outcomeSnapshots = useMemo(() => {
    if (!latestHistoryEntry) return [];
    const base = buildPlayerSnapshotsFromHistoryEntry(latestHistoryEntry, {
      heroUserId: heroUserId || null,
    });
    const tpl = Array.isArray(table?.players) ? table.players : [];
    return base.map((row) => {
      const tp = tpl[row.seatIndex];
      const uid = tp?.userId != null ? String(tp.userId) : '';
      return {
        ...row,
        avatarUrl: String(
          tp?.avatarUrl || (heroUserId && uid === heroUserId ? user?.avatarUrl : '') || ''
        ).trim(),
        seatIsBot: Boolean(tp?.isBot),
      };
    });
  }, [latestHistoryEntry, table?.players, heroUserId, user?.avatarUrl]);
  const outcomeKind = useMemo(() => {
    const winners = Array.isArray(latestHistoryEntry?.winners) ? latestHistoryEntry.winners : [];
    if (winners.length > 1) return 'chop';
    if (!heroUserId) return winners.length > 0 ? 'lose' : null;
    const heroWon = winners.some((w) => String(w?.userId || '') === String(heroUserId));
    return heroWon ? 'win' : winners.length > 0 ? 'lose' : null;
  }, [latestHistoryEntry, heroUserId]);
  const outcomeWinnerSummary = useMemo(() => {
    const winners = Array.isArray(latestHistoryEntry?.winners) ? latestHistoryEntry.winners : [];
    if (winners.length === 0) return '';
    const names = winners.map((w) => String(w?.username || w?.name || '—')).join(', ');
    if (winners.length > 1) return `Ничья: ${names}`;
    return `Победитель: ${names}`;
  }, [latestHistoryEntry]);
  const outcomeHeroDelta = useMemo(() => {
    const deltas = Array.isArray(latestHistoryEntry?.stackDeltas) ? latestHistoryEntry.stackDeltas : [];
    if (!deltas.length) return null;
    const byId = deltas.find((d) => String(d?.playerId || '') === String(heroUserId));
    if (byId) return Number(byId?.delta || 0);
    const heroName = table?.players?.[currentPlayerIndex]?.username
      ? String(table.players[currentPlayerIndex].username).trim()
      : '';
    const byName = heroName ? deltas.find((d) => String(d?.playerName || '').trim() === heroName) : null;
    if (byName) return Number(byName?.delta || 0);
    return null;
  }, [latestHistoryEntry, heroUserId, table?.players, currentPlayerIndex]);

  const outcomeSortedDeltas = useMemo(() => {
    const deltas = Array.isArray(latestHistoryEntry?.stackDeltas) ? latestHistoryEntry.stackDeltas : [];
    return [...deltas]
      .map((d) => ({
        ...d,
        deltaNum: Number(d?.delta || 0),
        endNum: Number(d?.chipsAfter ?? d?.endChips ?? 0),
      }))
      .sort((a, b) => b.deltaNum - a.deltaNum);
  }, [latestHistoryEntry]);

  /** Все занятые места за столом + дельта из последней записи истории (паритет «все игроки»). */
  const outcomeAllSeatsRows = useMemo(() => {
    const players = Array.isArray(table?.players) ? table.players : [];
    const deltas = Array.isArray(latestHistoryEntry?.stackDeltas) ? latestHistoryEntry.stackDeltas : [];
    const deltaById = new Map(deltas.map((d) => [String(d?.playerId || ''), d]));
    const histPlayers = Array.isArray(latestHistoryEntry?.players) ? latestHistoryEntry.players : [];
    const rows = [];
    players.forEach((p, seatIndex) => {
      if (!p || !p.userId) return;
      const uid = String(p.userId);
      const d = deltaById.get(uid);
      const hp = histPlayers.find((h) => String(h?.id || h?.userId || '') === uid);
      const chipsAfter = Number(d?.endChips ?? hp?.chips ?? p.chips ?? 0);
      const deltaNum = d != null ? Number(d.delta || 0) : null;
      rows.push({
        key: uid,
        seatIndex,
        name: (p.username && String(p.username).trim()) || 'Игрок',
        avatarUrl: String(
          p.avatarUrl || (heroUserId && uid === heroUserId ? user?.avatarUrl : '') || ''
        ).trim(),
        chipsAfter,
        deltaNum,
      });
    });
    return rows.sort((a, b) => (b.deltaNum ?? -9e15) - (a.deltaNum ?? -9e15));
  }, [table?.players, latestHistoryEntry, heroUserId, user?.avatarUrl]);

  /** Положительная дельта героя за раздачу (для блока «получили фишки»). */
  const heroGainSeka = useMemo(() => {
    if (!heroUserId) return null;
    const row = outcomeAllSeatsRows.find((r) => String(r.key) === String(heroUserId));
    if (row?.deltaNum != null && Number.isFinite(Number(row.deltaNum)) && Number(row.deltaNum) > 0) {
      return Math.floor(Number(row.deltaNum));
    }
    if (outcomeHeroDelta != null && Number.isFinite(Number(outcomeHeroDelta)) && Number(outcomeHeroDelta) > 0) {
      return Math.floor(Number(outcomeHeroDelta));
    }
    return null;
  }, [outcomeAllSeatsRows, heroUserId, outcomeHeroDelta]);

  const nextHandReadyIdSet = useMemo(
    () => new Set((Array.isArray(gameState.nextHandReadyUserIds) ? gameState.nextHandReadyUserIds : []).map(String)),
    [gameState.nextHandReadyUserIds]
  );

  const nextHandVoteTotal = outcomeAllSeatsRows.length;
  const nextHandReadyCount = useMemo(() => {
    return outcomeAllSeatsRows.filter((r) => nextHandReadyIdSet.has(r.key)).length;
  }, [outcomeAllSeatsRows, nextHandReadyIdSet]);

  const heroReadyForNextHand = Boolean(heroUserId && nextHandReadyIdSet.has(String(heroUserId)));

  const handleReadyNextHand = useCallback(() => {
    if (!heroUserId || heroReadyForNextHand) return;
    gameTransportRef.current?.emit('READY', {});
  }, [heroUserId, heroReadyForNextHand]);

  const nextHandDeadlineAtMs = Number(gameState.nextHandReadyDeadlineAtMs) || null;
  const nextHandBudgetMs = Math.max(1000, Number(gameState.nextHandReadyBudgetMs) || 30_000);
  const [nextHandUiNowMs, setNextHandUiNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!showHandOutcomeModal || !gameState.gameEnded || !nextHandDeadlineAtMs) return undefined;
    const id = setInterval(() => setNextHandUiNowMs(Date.now()), 250);
    return () => clearInterval(id);
  }, [showHandOutcomeModal, gameState.gameEnded, nextHandDeadlineAtMs]);

  const nextHandRemainingMs = useMemo(() => {
    if (!nextHandDeadlineAtMs) return 0;
    return Math.max(0, nextHandDeadlineAtMs - nextHandUiNowMs);
  }, [nextHandDeadlineAtMs, nextHandUiNowMs]);

  const nextHandCountdownRatio = useMemo(
    () => (nextHandBudgetMs > 0 ? Math.min(1, Math.max(0, nextHandRemainingMs / nextHandBudgetMs)) : 0),
    [nextHandBudgetMs, nextHandRemainingMs]
  );

  const showNextHandVoteInOutcomeModal = Boolean(
    table?.state === 'finished' && gameState.gameEnded && nextHandVoteTotal >= 2
  );

  const canOfferGraceExitBetweenHands = Boolean(
    returnView === 'online-lobby' &&
      showNextHandVoteInOutcomeModal &&
      heroGraceChipNet != null &&
      Number.isFinite(Number(heroGraceChipNet)) &&
      Number(gameState.handSeq) > 0 &&
      gameState.graceExit?.waveAppliedHandSeq !== gameState.handSeq
  );
  const graceExitHeroInProfit = Number(heroGraceChipNet) > 0;

  const handleGraceExitLeave = useCallback(() => {
    if (!canOfferGraceExitBetweenHands) return;
    graceExitInitiatorPendingRef.current = true;
    gameTransportRef.current?.emit('SEKA_GRACE_EXIT', {});
  }, [canOfferGraceExitBetweenHands]);

  const outcomeDetailMoves = useMemo(() => {
    if (Array.isArray(latestHistoryEntry?.actions) && latestHistoryEntry.actions.length > 0) {
      return latestHistoryEntry.actions;
    }
    return handMoves;
  }, [latestHistoryEntry, handMoves]);
  const chronicleSteps = useMemo(
    () =>
      handMoves.slice(-10).map((m) => {
        const name = typeof m.playerName === 'string' ? m.playerName : '—';
        return `${name}: ${formatHistoryAction(m)}`;
      }),
    [handMoves]
  );

  const serverChronicleHands = useMemo(() => {
    if (!Array.isArray(serverGameHistory) || serverGameHistory.length === 0) return [];
    return serverGameHistory.map((h, idx) => {
      const actions = Array.isArray(h?.actions) ? h.actions : [];
      const lines = actions.map((a) => {
        const name = typeof a?.playerName === 'string' && a.playerName ? a.playerName : '—';
        return `${name}: ${formatHistoryAction(a)}`;
      });
      return {
        handSeq: Number(gameState.handSeq || 0) - (serverGameHistory.length - idx - 1),
        lines,
      };
    });
  }, [serverGameHistory, gameState.handSeq]);
  const effectiveChronicleHands = useMemo(
    () => (serverChronicleHands.length > 0 ? serverChronicleHands : matchChronicleHands),
    [serverChronicleHands, matchChronicleHands]
  );

  const heroDisplayNameForFilter = useMemo(() => {
    if (table?.players && currentPlayerIndex >= 0) {
      const u = table.players[currentPlayerIndex]?.username;
      if (u && String(u).trim()) return String(u).trim();
    }
    return user?.username != null ? String(user.username).trim() : '';
  }, [table?.players, currentPlayerIndex, user?.username]);

  const matchChronicleHandsFilteredForModal = useMemo(() => {
    if (!matchSessionChronicleHeroOnly || !heroDisplayNameForFilter) return effectiveChronicleHands;
    const prefix = `${heroDisplayNameForFilter}:`;
    return effectiveChronicleHands.filter((h) => (h.lines || []).some((line) => line.startsWith(prefix)));
  }, [effectiveChronicleHands, matchSessionChronicleHeroOnly, heroDisplayNameForFilter]);

  const matchChronicleFlatStepsForModal = useMemo(() => {
    const out = [];
    matchChronicleHandsFilteredForModal.forEach((h) => {
      out.push(`Раздача №${h.handSeq}`);
      h.lines.forEach((line) => out.push(line));
    });
    return out;
  }, [matchChronicleHandsFilteredForModal]);

  /** Полная сводка по раздачам из `gameHistory` сервера (карты, дельты, ходы) — паритет с LocalGame. */
  const matchSessionFullHandRows = useMemo(() => {
    if (!Array.isArray(serverGameHistory) || serverGameHistory.length === 0) return [];
    const summaries = Array.isArray(serverCompletedHandSummaries) ? serverCompletedHandSummaries : [];
    return serverGameHistory.map((hist, idx) => {
      const sum = idx < summaries.length ? summaries[idx] : null;
      const handSeqFromSummary = sum != null ? Number(sum.handSeq) : 0;
      const handSeqFallback = Math.max(1, Number(gameState.handSeq || 0) - (serverGameHistory.length - idx - 1));
      const handSeq = handSeqFromSummary > 0 ? handSeqFromSummary : handSeqFallback;
      const winners = Array.isArray(hist.winners) ? hist.winners : [];
      const winnerNames =
        winners.length > 0
          ? winners.map((w) => String(w.username || w.name || w.userId || '—')).join(' · ')
          : hist.winner
            ? String(hist.winner.username || hist.winner.name || hist.winner.userId || '—')
            : 'Ничья';
      const deltas = Array.isArray(hist.stackDeltas) ? hist.stackDeltas : [];
      const heroDeltaObj = heroUserId
        ? deltas.find((d) => String(d.playerId || '') === String(heroUserId))
        : null;
      const heroDelta = Number(heroDeltaObj?.delta || 0);
      const sortedDeltas = [...deltas].sort((a, b) => Number(b.delta || 0) - Number(a.delta || 0));
      const actions = Array.isArray(hist.actions) ? hist.actions : [];
      const endType = describeSekaHandEndType(hist, actions);
      const hasHeroActivity = Boolean(
        heroUserId && actions.some((a) => String(a.playerId || '') === String(heroUserId))
      );
      const playerSnapshots = buildPlayerSnapshotsFromHistoryEntry(hist, {
        heroUserId: heroUserId || null,
      });
      return {
        id: `online-full-${handSeq}-${hist.timestamp ?? idx}`,
        handSeq,
        winnerNames,
        pot: Number(hist.pot || 0),
        timestamp: hist.timestamp != null ? new Date(Number(hist.timestamp)) : null,
        deltas: sortedDeltas.map((d) => ({
          playerId: String(d.playerId || ''),
          playerName: String(d.playerName || ''),
          delta: Number(d.delta || 0),
        })),
        actions,
        heroDelta,
        endType,
        hasHeroActivity,
        playerSnapshots,
      };
    });
  }, [serverGameHistory, serverCompletedHandSummaries, gameState.handSeq, heroUserId]);

  const matchSessionFullHandRowsFiltered = useMemo(() => {
    if (!matchSessionChronicleHeroOnly || !heroUserId) return matchSessionFullHandRows;
    return matchSessionFullHandRows.filter((r) => r.hasHeroActivity || r.heroDelta !== 0);
  }, [matchSessionFullHandRows, matchSessionChronicleHeroOnly, heroUserId]);

  useEffect(() => {
    const hs = gameState.handSeq ?? 0;
    const moves = handMoves;
    if (lastHandSeqChronicleRef.current !== hs) {
      if (lastHandSeqChronicleRef.current > 0 && hs > lastHandSeqChronicleRef.current) {
        const archived = lastHandMovesSnapshotRef.current;
        if (archived.length > 0) {
          setMatchChronicleHands((prev) =>
            [
              ...prev,
              {
                handSeq: lastHandSeqChronicleRef.current,
                lines: archived.map((m) => {
                  const name = typeof m.playerName === 'string' ? m.playerName : '—';
                  return `${name}: ${formatHistoryAction(m)}`;
                }),
              },
            ].slice(-20)
          );
        }
      }
      lastHandSeqChronicleRef.current = hs;
    }
    lastHandMovesSnapshotRef.current = moves;
  }, [gameState.handSeq, handMoves]);

  useEffect(() => {
    if (table?.state === 'waiting') {
      setMatchChronicleHands([]);
      lastHandSeqChronicleRef.current = 0;
      lastHandMovesSnapshotRef.current = [];
    }
  }, [table?.state]);

  useEffect(() => {
    if (table?.state === 'playing' && !gameState.gameEnded) {
      const p = Number(gameState.pot || 0);
      if (p > peakPotRef.current) peakPotRef.current = p;
    }
  }, [table?.state, gameState.gameEnded, gameState.pot]);

  useEffect(() => {
    if (!gameState.gameEnded) {
      lastFlyingAwardedHandSeqRef.current = -1;
    }
  }, [gameState.gameEnded, gameState.handSeq]);

  useEffect(() => {
    if (!gameState.gameEnded || !isOnlineTableHandSettled(table)) return;
    const hs = Number(gameState.handSeq || 0);
    if (hs <= 0 || lastOutcomeSoundHandSeqRef.current === hs) return;
    lastOutcomeSoundHandSeqRef.current = hs;
    if (outcomeKind !== 'win' && winnerFanfareFallbackTimerRef.current) {
      clearTimeout(winnerFanfareFallbackTimerRef.current);
      winnerFanfareFallbackTimerRef.current = null;
    }
    initAudio();
    if (outcomeKind === 'win') {
      if (winnerFanfareFallbackTimerRef.current) {
        clearTimeout(winnerFanfareFallbackTimerRef.current);
        winnerFanfareFallbackTimerRef.current = null;
      }
      winnerFanfarePlayAfterSessionOverlayCloseRef.current = false;
      const WIN_FANFARE_FALLBACK_MS = 2800;
      winnerFanfareFallbackTimerRef.current = setTimeout(() => {
        winnerFanfareFallbackTimerRef.current = null;
        if (!sessionOutcomeOverlayRef.current) {
          play('winner-game', 1);
        }
      }, WIN_FANFARE_FALLBACK_MS);
      return () => {
        if (winnerFanfareFallbackTimerRef.current) {
          clearTimeout(winnerFanfareFallbackTimerRef.current);
          winnerFanfareFallbackTimerRef.current = null;
        }
      };
    }
    if (outcomeKind === 'lose') {
      const heavyLoss = Number(outcomeHeroDelta || 0) <= -Math.max(100, Math.floor(Number(table?.buyIn || 0) * 0.35));
      setTimeout(() => play(heavyLoss ? 'lose-heavy' : 'lose-game', 1), 60);
      return;
    }
    if (outcomeKind === 'chop') {
      setTimeout(() => play('win', 0.85), 60);
    }
  }, [
    gameState.gameEnded,
    gameState.handSeq,
    table,
    outcomeKind,
    outcomeHeroDelta,
    table?.buyIn,
    initAudio,
    play,
  ]);

  useEffect(() => {
    if (!gameState.gameEnded || !isOnlineTableHandSettled(table)) return;
    const hs = gameState.handSeq ?? 0;
    if (lastFlyingAwardedHandSeqRef.current === hs) return;
    const winners = gameState.winners;
    const w = gameState.winner;
    const heroWon =
      (Array.isArray(winners) &&
        winners.some((x) => x && String(x.userId) === heroUserId)) ||
      (w && String(w.userId) === heroUserId);
    if (!heroUserId || !heroWon) {
      peakPotRef.current = 0;
      return;
    }
    lastFlyingAwardedHandSeqRef.current = hs;
    setSessionHeroWins((w) => w + 1);
    const amt = Math.max(80, Math.min(peakPotRef.current || 200, 8000));
    peakPotRef.current = 0;
    const nid = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setNotifications((prev) => [
      ...prev,
      {
        id: nid,
        message: `Вы выиграли банк ~${amt.toLocaleString('ru-RU')} SEKA`,
        type: 'success',
      },
    ]);
    if (prefersReducedMotion || !potAnchorRef.current || !heroSeatAnchorRef.current) {
      return;
    }
    setFlyingCoins({
      amount: amt,
      from: potAnchorRef.current,
      to: heroSeatAnchorRef.current,
    });
    setShowFlyingCoins(true);
  }, [
    gameState.gameEnded,
    gameState.handSeq,
    gameState.winner,
    gameState.winners,
    heroUserId,
    table,
    prefersReducedMotion,
  ]);

  useEffect(() => {
    if (!gameState.gameEnded || !isOnlineTableHandSettled(table)) return;
    const hs = gameState.handSeq ?? 0;
    if (hs <= 0 || lastHandOutcomeHandledSeqRef.current === hs) return;
    lastHandOutcomeHandledSeqRef.current = hs;
    setShowHandOutcomeModal(true);
  }, [gameState.gameEnded, gameState.handSeq, table]);

  useEffect(() => {
    if (!gameState.gameEnded) setShowHandOutcomeModal(false);
  }, [gameState.gameEnded]);

  useEffect(() => {
    if (!heroUserId) {
      setSessionTotalHands(serverCompletedHandSummaries.length);
      setSessionHeroWins(0);
      setSessionHeroLosses(0);
      return;
    }
    const uid = String(heroUserId);
    const rows = serverCompletedHandSummaries;
    const wins = rows.filter((h) =>
      Array.isArray(h?.winners) ? h.winners.some((w) => String(w?.userId) === uid) : false
    ).length;
    const losses = rows.filter((h) => {
      const involved = Array.isArray(h?.seats) ? h.seats.some((s) => String(s?.userId) === uid) : false;
      if (!involved) return false;
      const won = Array.isArray(h?.winners) ? h.winners.some((w) => String(w?.userId) === uid) : false;
      return !won;
    }).length;
    setSessionTotalHands(rows.length);
    setSessionHeroWins(wins);
    setSessionHeroLosses(losses);
  }, [serverCompletedHandSummaries, heroUserId]);

  const handleCopyHandOutcome = useCallback(async () => {
    const text = latestHistoryEntry
      ? buildSimpleSekaOutcomeExportText([], latestHistoryEntry, { heroUserId })
      : buildOnlineHandOutcomeExportText({
          tableName: table?.name,
          handSeq: gameState.handSeq ?? 0,
          pot: gameState.pot,
          winners: gameState.winners,
          winner: gameState.winner,
          handMoves,
          heroUserId,
          historyEntry: latestHistoryEntry || null,
        });
    try {
      await navigator.clipboard.writeText(text);
      setHandOutcomeCopyFeedback(true);
      if (handOutcomeCopyTimerRef.current) clearTimeout(handOutcomeCopyTimerRef.current);
      handOutcomeCopyTimerRef.current = setTimeout(() => {
        setHandOutcomeCopyFeedback(false);
        handOutcomeCopyTimerRef.current = null;
      }, 2200);
    } catch {
      const nid = `hand-outcome-copy-fail-${Date.now()}`;
      setNotifications((prev) => [
        ...prev,
        { id: nid, message: 'Не удалось скопировать текст', type: 'info' },
      ]);
    }
  }, [
    latestHistoryEntry,
    table?.name,
    gameState.handSeq,
    gameState.pot,
    gameState.winners,
    gameState.winner,
    handMoves,
    heroUserId,
  ]);

  useEffect(() => {
    if (!canAct) setShowRaiseInput(false);
  }, [canAct]);

  useEffect(() => {
    if (!raiseRange) return;
    setSekaRaiseAmount((v) => {
      const next = Number.isFinite(v) && v > 0 ? v : raiseRange.min;
      return Math.min(raiseRange.max, Math.max(raiseRange.min, next));
    });
  }, [raiseRange]);

  useEffect(() => {
    setShowRaiseInput(false);
    setLiveMovesOpen(false);
  }, [gameState.handSeq]);

  useEffect(() => {
    const hs = gameState.handSeq ?? 0;
    if (!table || table.state !== 'playing' || hs <= 0) return;
    if (hs !== prevHandSeqFxRef.current) {
      prevHandSeqFxRef.current = hs;
      setDeckPulseKey((k) => k + 1);
      if (prefersReducedMotion) play('card-deal');
    }
  }, [gameState.handSeq, table, prefersReducedMotion, play]);

  const flushOnlineDealFlightArtifacts = useCallback(() => {
    dealFlightAnimsRef.current.forEach((a) => {
      try {
        a.cancel();
      } catch {
        /* ignore */
      }
    });
    dealFlightAnimsRef.current = [];
    dealFlightRootsRef.current.forEach(({ root, el }) => {
      try {
        root.unmount();
      } catch {
        /* ignore */
      }
      try {
        el.remove();
      } catch {
        /* ignore */
      }
    });
    dealFlightRootsRef.current = [];
  }, []);

  useEffect(() => {
    const t = tableRefForDealFx.current;
    if (prefersReducedMotion || deckPulseKey <= 0 || !t || t.state !== 'playing') return;

    dealGenRef.current += 1;
    const gen = dealGenRef.current;
    dealFlightAbortRef.current = false;

    const run = async () => {
      await new Promise((r) => {
        requestAnimationFrame(() => requestAnimationFrame(r));
      });
      if (dealGenRef.current !== gen || dealFlightAbortRef.current) return;

      const deckEl = deckFlightRef.current;
      const slots = seatLayoutSlotsRef.current || [];
      if (!deckEl || slots.length < 2) return;

      for (let si = 0; si < slots.length; si += 1) {
        const seat = slots[si].seat;
        for (let c = 0; c < 3; c += 1) {
          if (dealFlightAbortRef.current || dealGenRef.current !== gen) return;

          play('card-deal', ONLINE_DEAL_CARD_SOUND_VOL);

          const row = seatCardSlotRefsRef.current[seat];
          const targetRef = row?.[c];
          const targetEl = targetRef?.current;
          const deckRect = deckEl.getBoundingClientRect();
          if (!targetEl || deckRect.width < 2) continue;

          let targetRect = targetEl.getBoundingClientRect();
          if (targetRect.width < 2) continue;

          const wrapper = document.createElement('div');
          wrapper.style.position = 'fixed';
          wrapper.style.left = `${deckRect.left}px`;
          wrapper.style.top = `${deckRect.top}px`;
          wrapper.style.width = `${deckRect.width}px`;
          wrapper.style.height = `${deckRect.height}px`;
          wrapper.style.zIndex = '60';
          wrapper.style.pointerEvents = 'none';
          wrapper.style.transformOrigin = 'top left';
          document.body.appendChild(wrapper);

          const root = createRoot(wrapper);
          root.render(<PlayingCard faceDown size="sm" />);
          dealFlightRootsRef.current.push({ root, el: wrapper });

          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              if (dealFlightAbortRef.current || dealGenRef.current !== gen) {
                try {
                  root.unmount();
                } catch {
                  /* ignore */
                }
                try {
                  wrapper.remove();
                } catch {
                  /* ignore */
                }
                dealFlightRootsRef.current = dealFlightRootsRef.current.filter((x) => x.el !== wrapper);
                resolve();
                return;
              }

              targetRect = targetEl.getBoundingClientRect();
              const wRect = wrapper.getBoundingClientRect();
              const tx = targetRect.left - wRect.left;
              const ty = targetRect.top - wRect.top;
              const sx = targetRect.width / Math.max(wRect.width, 1);
              const sy = targetRect.height / Math.max(wRect.height, 1);

              const anim = wrapper.animate(
                [
                  { transform: 'translate(0px, 0px) scale(1, 1)' },
                  { transform: `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})` },
                ],
                {
                  duration: ONLINE_DEAL_FLIGHT_BURST_MS,
                  easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                  fill: 'forwards',
                }
              );
              dealFlightAnimsRef.current.push(anim);

              anim.finished
                .catch(() => {})
                .finally(() => {
                  dealFlightAnimsRef.current = dealFlightAnimsRef.current.filter((x) => x !== anim);
                  try {
                    root.unmount();
                  } catch {
                    /* ignore */
                  }
                  try {
                    wrapper.remove();
                  } catch {
                    /* ignore */
                  }
                  dealFlightRootsRef.current = dealFlightRootsRef.current.filter((x) => x.el !== wrapper);
                  resolve();
                });
            });
          });
        }
        if (si < slots.length - 1) {
          if (dealFlightAbortRef.current || dealGenRef.current !== gen) return;
          await new Promise((r) => {
            setTimeout(r, ONLINE_DEAL_BETWEEN_PLAYERS_MS);
          });
        }
      }
    };

    void run();

    return () => {
      dealFlightAbortRef.current = true;
      dealGenRef.current += 1;
      flushOnlineDealFlightArtifacts();
    };
  }, [deckPulseKey, prefersReducedMotion, play, flushOnlineDealFlightArtifacts]);

  const onBarAction = useCallback(
    (action) => {
      if (!canAct) return;
      const allowed = getAvailableActions();
      const actionUpper = String(action || '').toUpperCase();
      if (!allowed.some((a) => String(a?.action || '').toUpperCase() === actionUpper)) return;
      if (String(action).toUpperCase() === 'RAISE') {
        const p = table?.players?.[turnSeatIndex];
        if (!p) return;
        const step = Math.max(1, effectiveTableMinRaise);
        const cb = Number(gameState.currentBet) || 0;
        const minR = cb + step;
        const chips = Number(p.chips || 0);
        const maxR = Math.min(chips * (p.isDark ? 2 : 1), cb + chips);
        setSekaRaiseAmount(Math.min(maxR, Math.max(minR, minR)));
        setShowRaiseInput(true);
        return;
      }
      handlePlayerMove(action);
    },
    [
      canAct,
      table,
      turnSeatIndex,
      gameState.currentBet,
      effectiveTableMinRaise,
      handlePlayerMove,
      getAvailableActions,
    ]
  );

  const heroHoleStrings = useMemo(() => {
    if (!heroUserId || !gameState.playerCards) return null;
    const c = gameState.playerCards[heroUserId];
    return Array.isArray(c) ? c : null;
  }, [heroUserId, gameState.playerCards]);

  const heroHandDescription = useMemo(() => {
    if (!heroHoleStrings || heroHoleStrings.length !== 3) return undefined;
    return evaluateSekaHand(heroHoleStrings).description;
  }, [heroHoleStrings]);

  const heroHoleStripCardKeys = useMemo(() => {
    const hs = Number(gameState.handSeq ?? 0);
    const he = Number(gameState.holeEpoch ?? 0);
    return [0, 1, 2].map((i) => `online-${hs}-${he}-${i}`);
  }, [gameState.handSeq, gameState.holeEpoch]);

  const handleCopyMatchChronicle = useCallback(async () => {
    const chronicleForExport =
      matchSessionChronicleHeroOnly && heroUserId
        ? matchChronicleHandsFilteredForModal
        : effectiveChronicleHands;
    const text = buildOnlineMatchChronicleExportText(
      chronicleForExport,
      handMoves,
      gameState.handSeq ?? 0
    );
    try {
      await navigator.clipboard.writeText(text);
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      setNotifications((prev) => [
        ...prev,
        { id, message: 'Хроника скопирована в буфер', type: 'success' },
      ]);
    } catch {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      setNotifications((prev) => [...prev, { id, message: 'Не удалось скопировать', type: 'error' }]);
    }
  }, [
    effectiveChronicleHands,
    matchChronicleHandsFilteredForModal,
    matchSessionChronicleHeroOnly,
    heroUserId,
    handMoves,
    gameState.handSeq,
  ]);

  const handleCopyFullSession = useCallback(async () => {
    const chronicleForExport =
      matchSessionChronicleHeroOnly && heroUserId
        ? matchChronicleHandsFilteredForModal
        : effectiveChronicleHands;
    const fullHandRowsForExport =
      matchSessionChronicleHeroOnly && heroUserId
        ? matchSessionFullHandRowsFiltered
        : matchSessionFullHandRows;
    const text = buildOnlineFullSessionExportText({
      tableName: table?.name,
      blindsLabel: getBlindsLabel(table),
      tableBuyIn: table?.buyIn,
      sessionTotalHands,
      sessionHeroWins,
      sessionHeroLosses,
      chronicleArchivedCount: chronicleForExport.length,
      matchChronicleHands: chronicleForExport,
      currentHandMoves: handMoves,
      currentHandSeq: gameState.handSeq ?? 0,
      completedHandSummaries: serverCompletedHandSummariesFiltered,
      fullHandRows: fullHandRowsForExport,
      allHandRowsForEndTypeMap: matchSessionFullHandRows,
    });
    try {
      await navigator.clipboard.writeText(text);
      setMatchSessionFullCopyFeedback(true);
      if (matchSessionCopyTimerRef.current) clearTimeout(matchSessionCopyTimerRef.current);
      matchSessionCopyTimerRef.current = setTimeout(() => {
        setMatchSessionFullCopyFeedback(false);
        matchSessionCopyTimerRef.current = null;
      }, 2200);
    } catch {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      setNotifications((prev) => [...prev, { id, message: 'Не удалось скопировать', type: 'error' }]);
    }
  }, [
    table,
    sessionTotalHands,
    sessionHeroWins,
    sessionHeroLosses,
    effectiveChronicleHands,
    matchChronicleHandsFilteredForModal,
    matchSessionChronicleHeroOnly,
    heroUserId,
    handMoves,
    gameState.handSeq,
    serverCompletedHandSummariesFiltered,
    matchSessionFullHandRows,
    matchSessionFullHandRowsFiltered,
  ]);

  const rawMine = heroHoleStrings;

  if (loading) {
    return (
      <div className="pt-root min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-neon-cyan border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Загрузка стола…</p>
        </div>
      </div>
    );
  }

  if (error || !table) {
    return (
      <div className="pt-root min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4 rounded-xl border border-border bg-card/90 p-8 backdrop-blur-md">
          <div className="text-3xl">⚠️</div>
          <h2 className="text-lg font-bold text-foreground">Стол недоступен</h2>
          <p className="text-sm text-muted-foreground">{error || 'Стол не найден или нет доступа.'}</p>
          <button
            type="button"
            onClick={() => {
              window.location.href = '/';
            }}
            className="px-4 py-2 rounded-lg bg-neon-cyan/20 border border-neon-cyan/50 text-neon-cyan text-sm font-semibold hover:bg-neon-cyan/30"
          >
            Назад в лобби
          </button>
        </div>
      </div>
    );
  }

  const blindsLabel = getBlindsLabel(table);
  const communitySlots = buildCommunitySlots(gameState.communityCards);
  const hero =
    currentPlayerIndex >= 0 ? table.players[currentPlayerIndex] : table.players[0];
  const parsedMine = (rawMine || []).map(parseSekaCard).filter(Boolean);
  const hasHeroPrivateCards = parsedMine.length > 0;
  const showUserHoles =
    hasHeroPrivateCards &&
    (!hero?.isDark || Boolean(gameState.gameEnded) || table.state === 'finished');
  const userHoleModels = showUserHoles ? parsedMine : dummyHoles(3);
  const userStack = Number(hero?.chips ?? hero?.balance ?? 0);
  const anteBurstBySeat = new Map(
    anteSeatBursts.map((burst) => [Number(burst.seatIndex), burst])
  );
  return (
    <div className="pt-root relative flex min-h-screen flex-col overflow-hidden bg-background text-foreground">
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-cyan/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-orange/40 to-transparent" />
        <div className="absolute top-0 bottom-0 left-0 w-px bg-gradient-to-b from-neon-cyan/20 via-transparent to-neon-orange/20" />
        <div className="absolute top-0 bottom-0 right-0 w-px bg-gradient-to-b from-neon-cyan/20 via-transparent to-neon-orange/20" />
      </div>

      <GameIndustrialHeader
        floating
        tableName={table.name}
        blindsLabel={blindsLabel}
        playersCount={table.players?.length ?? 0}
        maxPlayers={table.maxPlayers ?? 6}
        bucksBalance={user?.bucksBalance}
        balance={user?.balance}
        onLeave={handleHeaderLeave}
        seatArrangeMode={seatArrangeMode}
        onSeatArrangeToggle={() => setSeatArrangeMode((v) => !v)}
        onSeatLayoutReset={handleClearOnlineSeatOffsets}
      />

      {(sessionTotalHands > 0 ||
        sessionHeroWins > 0 ||
        sessionHeroLosses > 0 ||
        effectiveChronicleHands.length > 0 ||
        handMoves.length > 0) &&
      table.state !== 'waiting' ? (
        <button
          type="button"
          title="Открыть сводку матча и хронику"
          onClick={() => setShowMatchSessionModal(true)}
          className="relative z-10 w-full border-b border-white/5 bg-black/15 px-3 py-1.5 text-center text-[10px] text-muted-foreground transition hover:bg-black/25 hover:text-foreground"
        >
          <span className="font-mono tabular-nums">Раздач: {sessionTotalHands}</span>
          {sessionHeroWins > 0 ? (
            <>
              {' · '}
              Побед: <span className="font-mono font-semibold text-amber-200/95">{sessionHeroWins}</span>
            </>
          ) : null}
          {sessionHeroLosses > 0 ? (
            <>
              {' · '}
              Поражений:{' '}
              <span className="font-mono font-semibold text-rose-200/90">{sessionHeroLosses}</span>
            </>
          ) : null}
          <span className="mt-0.5 block text-[9px] font-medium text-amber-200/70">
            Нажмите для полной сводки матча
          </span>
        </button>
      ) : null}

      <main className="relative z-10 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 py-3">
        {(anteFlights.length > 0 || antePhaseText) ? (
          <div className="pointer-events-none fixed inset-0 z-[120]" aria-live="polite">
            {anteFlights.map((flight) => (
              <React.Fragment key={flight.id}>
                <span
                  className="ante-fly-number"
                  style={{
                    left: `${flight.startX}px`,
                    top: `${flight.startY}px`,
                    '--ante-dx': `${flight.dx}px`,
                    '--ante-dy': `${flight.dy}px`,
                    animationDelay: `${flight.delayMs}ms`,
                  }}
                >
                  -{Number(flight.amount || 0).toLocaleString('ru-RU')} SEKA
                </span>
                <span
                  className="ante-fly-coin"
                  style={{
                    left: `${flight.startX}px`,
                    top: `${flight.startY}px`,
                    '--ante-dx': `${flight.coinDx}px`,
                    '--ante-dy': `${flight.coinDy}px`,
                    animationDelay: `${Math.max(0, Number(flight.delayMs || 0) + 55)}ms`,
                  }}
                  aria-hidden
                >
                  ◉
                </span>
              </React.Fragment>
            ))}
          </div>
        ) : null}
        {antePhaseText ? (
          <div className="pointer-events-none fixed left-1/2 top-[max(4.7rem,env(safe-area-inset-top,0px)+3.5rem)] z-[120] -translate-x-1/2 px-3">
            <div className="rounded-full border border-red-300/45 bg-gradient-to-r from-[#4d151a]/95 via-[#3a1016]/95 to-[#270a0f]/95 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.08em] text-red-100 shadow-[0_10px_28px_rgba(220,38,38,0.28)]">
              {antePhaseText}
            </div>
          </div>
        ) : null}

        <div
          ref={seatArenaRef}
          className={cn(
            'relative mx-auto w-full flex-1',
            seatLayoutMeta.arena.maxWidthClass,
            seatLayoutMeta.arena.minHeightClass,
            seatArrangeMode && 'rounded-xl ring-2 ring-amber-400/45 ring-offset-2 ring-offset-background'
          )}
        >
          <div className="absolute inset-0 flex items-center justify-center py-0.5">
            <PokerTableSurface
              ref={deckFlightRef}
              communitySlots={communitySlots}
              pot={gameState.pot || 0}
              potLabel="POT"
              showCommunity={false}
              // Keep online/local parity in active hand while preserving finished-state reveal.
              showDeck={isPlayingState || table.state === 'finished'}
              deckCount={gameState.deckRemaining}
              deckPulseKey={deckPulseKey}
              potAnchorRef={potAnchorRef}
            />
          </div>
          <div className="pointer-events-none absolute inset-0 z-10">
            {seatLayoutMeta.slots.map((slot) => {
              const seat = slot.seat;
              const p = table.players[seat];
              if (!p) return null;
              const effHero = currentPlayerIndex >= 0 ? currentPlayerIndex : 0;
              const nm =
                p.username && p.username.length > 12 ? `${p.username.slice(0, 11)}…` : p.username || 'Игрок';
              const uiCards = (() => {
                if (seat === effHero) return userHoleModels;
                const canRevealOppCards = Boolean(gameState.gameEnded || table.state === 'finished');
                if (!canRevealOppCards) return dummyHoles(3);
                const opp = (p.cards || []).map(parseSekaCard).filter(Boolean);
                return opp.length ? opp : dummyHoles(3);
              })();
              const showFace =
                seat === effHero
                  ? showUserHoles
                  : (() => {
                      const canRevealOppCards = Boolean(gameState.gameEnded || table.state === 'finished');
                      if (!canRevealOppCards) return false;
                      const opp = (p.cards || []).map(parseSekaCard).filter(Boolean);
                      return opp.length > 0 && !p.isFolded;
                    })();
              const disp = seatDisplayPercents(slot, seatOffsetBySeat[seat]);
              const tt = turnTimerForSeat(seat);
              if (!seatCardSlotRefsRef.current[seat]) {
                seatCardSlotRefsRef.current[seat] = [
                  React.createRef(),
                  React.createRef(),
                  React.createRef(),
                ];
              }
              if (!anteSourceRefsRef.current[seat]) {
                anteSourceRefsRef.current[seat] = React.createRef();
              }
              const cardSlotRefs = seatCardSlotRefsRef.current[seat];
              const heroPendingBuyInSeat = Number(p.pendingBuyIn || 0);
              const heroFooterOnline =
                seat === effHero && table.state === 'playing' && !gameState.gameEnded
                  ? heroPendingBuyInSeat > 0
                    ? `+${heroPendingBuyInSeat.toLocaleString('ru-RU')} в очереди`
                    : p.isSittingOut && Number(p.chips || 0) <= 0
                      ? 'Вне раздачи'
                      : p.isSittingOut && Number(p.chips || 0) > 0
                        ? 'Пропуск · наблюдаете'
                        : undefined
                  : undefined;
              const heroEliminatedCross =
                seat === effHero &&
                table.state === 'playing' &&
                !gameState.gameEnded &&
                Boolean(p.isSittingOut) &&
                Number(p.chips || 0) <= 0 &&
                heroPendingBuyInSeat <= 0;
              const isNearDeckSeat = seat === 3;
              const footerMode =
                seatLayoutMeta.density.hideSecondaryMeta ||
                seatLayoutMeta.density.compact ||
                isNearDeckSeat
                  ? 'minimal'
                  : 'full';
              const modeIndicatorLabel = p.isDark ? 'В ТЕМНУЮ' : 'В ОТКРЫТУЮ';
              const modeIndicatorState = p.isDark ? 'dark' : 'open';
              const nameActionBadge =
                !seatLayoutMeta.density.hideLastActionBadges && lastSeatActions[seat]?.kind ? (
                  <SeatLastMoveBadge
                    action={lastSeatActions[seat]}
                    isDimmed={Boolean(p.isFolded)}
                    compact={footerMode === 'minimal'}
                  />
                ) : null;
              return (
                <div
                  key={p.userId || `seat-${seat}`}
                  ref={seat === effHero ? heroSeatAnchorRef : undefined}
                  className="absolute"
                  style={{
                    left: `${disp.leftPct}%`,
                    top: `${disp.topPct}%`,
                    transform: `translate(-50%, -50%) scale(${seatLayoutMeta.density.seatScale})`,
                    transformOrigin: 'center',
                  }}
                >
                  <div
                    className="pointer-events-none absolute left-1/2 top-[36%] z-[5] flex -translate-x-1/2 gap-0.5"
                    aria-hidden
                  >
                    <span ref={cardSlotRefs[0]} className="h-[52px] w-[30px] sm:w-[34px]" />
                    <span ref={cardSlotRefs[1]} className="h-[52px] w-[30px] sm:w-[34px]" />
                    <span ref={cardSlotRefs[2]} className="h-[52px] w-[30px] sm:w-[34px]" />
                  </div>
                  {anteBurstBySeat.get(seat) ? (
                    <div
                      className="pointer-events-none absolute -top-2 left-1/2 z-[14] -translate-x-1/2 rounded-full border border-red-300/55 bg-red-500/20 px-2 py-0.5 text-[10px] font-black tabular-nums text-red-100 shadow-[0_0_14px_rgba(248,113,113,0.4)] ante-seat-burst"
                      style={{
                        animationDelay: `${Number(anteBurstBySeat.get(seat)?.delayMs || 0)}ms`,
                      }}
                    >
                      -{Number(anteBurstBySeat.get(seat)?.amount || 0).toLocaleString('ru-RU')}
                    </div>
                  ) : null}
                  <div
                    className={cn(
                      'relative flex flex-col items-center gap-0.5',
                      seatArrangeMode
                        ? 'pointer-events-auto cursor-grab touch-none active:cursor-grabbing'
                        : 'pointer-events-auto'
                    )}
                    onPointerDown={(ev) => handleSeatDragPointerDown(ev, seat, slot.leftPct, slot.topPct)}
                    onPointerMove={handleSeatDragPointerMove}
                    onPointerUp={handleSeatDragPointerUp}
                    onPointerCancel={handleSeatDragPointerUp}
                    onLostPointerCapture={handleSeatDragLostCapture}
                  >
                    <IndustrialSeat
                      name={nm}
                      chips={Number(p.chips ?? p.balance ?? 0)}
                      chipsDisplay={
                        seatLayoutMeta.density.hideSecondaryMeta
                          ? undefined
                          : Number(p.chips ?? p.balance ?? 0)
                      }
                      avatarUrl={String(
                        p.avatarUrl ||
                          (heroUserId && String(p.userId) === heroUserId ? user?.avatarUrl : '') ||
                          ''
                      ).trim()}
                      anteSourceRef={anteSourceRefsRef.current[seat]}
                      bet={p.bet || 0}
                      compact={seatLayoutMeta.density.compact}
                      cards={
                        seat === effHero && table.state === 'playing'
                          ? []
                          : uiCards
                      }
                      isDealer={Boolean(p.isDealer)}
                      isCurrentTurn={seat === turnSeatIndex}
                      turnTimeLeftSec={tt.left}
                      turnTimeTotalSec={tt.total}
                      turnTimeFraction={tt.frac}
                      isFolded={Boolean(p.isFolded)}
                      isWinner={Boolean(p.isWinner)}
                      showCards={showFace}
                      isUser={seat === effHero}
                      isBot={Boolean(p.isBot)}
                      seatColorIndex={seat}
                      showEliminatedCross={heroEliminatedCross}
                      footerLabel={heroFooterOnline}
                      modeIndicatorLabel={modeIndicatorLabel}
                      modeIndicatorState={modeIndicatorState}
                      nameActionBadge={nameActionBadge}
                      footerMode={footerMode}
                      suppressAvatarDragPropagation={seatArrangeMode}
                      onAvatarClick={() => {
                        const label = (p.username && String(p.username).trim()) || nm || 'Игрок';
                        const stack = Number(p.chips ?? p.balance ?? 0);
                        if (p.isBot) {
                          setSeatProfileOpen({ variant: 'bot', displayName: label, chips: stack });
                          return;
                        }
                        const seatAv = String(p.avatarUrl || '').trim();
                        if (seat === effHero) {
                          setSeatProfileOpen({
                            variant: 'self',
                            displayName: label,
                            chips: stack,
                            avatarUrl: seatAv || String(user?.avatarUrl || '').trim(),
                          });
                          return;
                        }
                        setSeatProfileOpen({
                          variant: 'human',
                          userId: p.userId,
                          displayName: label,
                          chips: stack,
                          avatarUrl: seatAv,
                        });
                      }}
                    >
                      {p.isBot && (
                        <FlickeringElement speed="slow">
                          <div className="mt-1 rounded border border-white/10 bg-black/50 px-1.5 py-0.5 text-[10px] text-green-400">
                            {p.botInfo?.personality || 'Бот'}
                          </div>
                        </FlickeringElement>
                      )}
                    </IndustrialSeat>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {gameState.currentBet > 0 && (
          <div className="text-center font-mono text-xs text-muted-foreground">
            Ставка: <span className="text-neon-orange">{gameState.currentBet} SEKA</span>
          </div>
        )}

        {Array.isArray(gameState.sidePots) && gameState.sidePots.length > 0 && (
          <div className="rounded-lg border border-border/60 bg-card/60 px-3 py-2 text-center text-[10px] text-muted-foreground">
            {gameState.sidePots.map((sp, i) => (
              <span key={i} className="block">
                Боковой банк {i + 1}: {Number(sp?.amount ?? sp ?? 0)} SEKA
              </span>
            ))}
            <p className="mt-1.5 text-[9px] leading-snug text-muted-foreground/80">
              Сводка по уровням вклада (при неравных стеках; сейчас в движке поле зарезервировано).
            </p>
          </div>
        )}

        {tableRulesHint ? (
          <p className="mx-auto max-w-lg px-1 text-center text-[10px] leading-snug text-muted-foreground">
            {tableRulesHint}
          </p>
        ) : null}

        <section className="mx-auto w-full max-w-lg rounded-xl border border-border/70 bg-card/70 p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Чат игры</h3>
          <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg bg-black/25 p-2">
            {gameChatMessages.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">Сообщений пока нет</p>
            ) : (
              gameChatMessages.map((m) => {
                const author = m?.author && typeof m.author === 'object' ? m.author : {};
                const messageId = String(m?.messageId || `${m?.at || 0}-${author?.userId || 'u'}`);
                const username = String(author?.username || author?.userId || 'Игрок');
                const rankName = String(author?.rankName || 'Первоход');
                const level = Number(author?.level) || 1;
                const avatarUrl = String(author?.avatarUrl || '').trim();
                return (
                  <div key={messageId} className="rounded-md bg-black/35 px-2 py-1.5 text-[11px]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-2 font-semibold text-foreground">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt={username}
                            className="h-5 w-5 rounded-full border border-white/15 object-cover"
                          />
                        ) : null}
                        <span className="truncate">{username}</span>
                      </span>
                      <span className="shrink-0 text-[10px] text-muted-foreground">
                        {rankName} · ур. {level}
                      </span>
                    </div>
                    <p className="mt-1 break-words text-foreground/95">{String(m?.text || '')}</p>
                  </div>
                );
              })
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={gameChatDraft}
              onChange={(e) => setGameChatDraft(e.target.value)}
              maxLength={400}
              placeholder="Сообщение..."
              className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-neon-cyan/45"
            />
            <button
              type="button"
              onClick={handleSendGameChat}
              disabled={!gameChatDraft.trim() || Date.now() < gameChatNextAtMs}
              className="rounded-lg border border-neon-cyan/40 bg-neon-cyan/10 px-3 py-2 text-xs font-semibold text-neon-cyan disabled:opacity-40"
            >
              Отпр.
            </button>
          </div>
        </section>

        {table.state === 'playing' && hero && hasHeroPrivateCards && (
          <PlayerHandStrip
            cards={userHoleModels}
            handName={
              showUserHoles ? heroHandDescription || gameState.handName || undefined : undefined
            }
            faceDown={!showUserHoles}
            cardKeys={heroHoleStripCardKeys}
            slotRefs={heroStripSlotRefs.current}
          />
        )}

        {(table.state === 'playing' || table.state === 'finished') && (
          <div className="sticky bottom-0 z-30 bg-gradient-to-t from-background via-background to-transparent pb-4 pt-2">
            {table.state === 'finished' &&
            gameState.gameEnded &&
            nextHandVoteTotal >= 2 &&
            !showHandOutcomeModal ? (
              <div className="mx-auto mb-2 w-full max-w-lg rounded-xl border border-cyan-500/30 bg-cyan-950/25 px-3 py-2.5 text-center shadow-inner">
                <p className="text-[11px] font-semibold text-cyan-100/95">
                  Следующая раздача: готово {nextHandReadyCount} из {nextHandVoteTotal}
                </p>
                <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
                  Когда все игроки за столом нажмут «Готов», сервер автоматически начнёт новую руку.
                </p>
                {heroUserId ? (
                  <button
                    type="button"
                    onClick={handleReadyNextHand}
                    disabled={heroReadyForNextHand}
                    className={cn(
                      'mt-2 w-full rounded-lg border px-3 py-2 text-xs font-bold uppercase tracking-wide transition',
                      heroReadyForNextHand
                        ? 'cursor-default border-emerald-400/45 bg-emerald-500/15 text-emerald-100'
                        : 'border-neon-cyan/50 bg-neon-cyan/15 text-neon-cyan hover:bg-neon-cyan/25'
                    )}
                  >
                    {heroReadyForNextHand ? 'Вы готовы' : 'Готов к следующей раздаче'}
                  </button>
                ) : null}
              </div>
            ) : null}
            {table.state === 'playing' &&
            !gameState.gameEnded &&
            currentPlayerIndex >= 0 &&
            heroSeatSnapshot &&
            heroSeatSnapshot.pendingBuyIn > 0 ? (
              <div className="mx-auto mb-2 max-w-lg rounded-lg border border-amber-400/30 bg-amber-950/30 px-3 py-2 text-center text-[11px] text-amber-100/95">
                Докупка в очереди:{' '}
                <span className="font-mono font-bold tabular-nums">
                  {heroSeatSnapshot.pendingBuyIn.toLocaleString('ru-RU')} SEKA
                </span>
                <span className="block text-[10px] font-normal text-amber-200/70">
                  Применится в следующей раздаче
                </span>
              </div>
            ) : null}
            {table.state === 'playing' && !gameState.gameEnded && showOnlineBuyInPanel ? (
              <div className="mx-auto mb-3 w-full max-w-lg rounded-xl border border-amber-500/35 bg-gradient-to-b from-amber-950/45 to-background px-3 py-3 shadow-[0_8px_28px_rgba(0,0,0,0.35)]">
                <p className="text-center text-xs font-bold text-amber-100">Нет фишек за столом</p>
                <p className="mt-1 text-center text-[11px] leading-snug text-muted-foreground">
                  Матч идёт без вас в раздаче: вы наблюдаете за столом. Докупите с баланса, чтобы снова получать
                  карты и ходить.
                </p>
                {canSubmitOnlineBuyIn ? (
                  <div className="mt-3 flex flex-col gap-2">
                    {userBalance >= 2 ? (
                      <>
                        <PokerBetRaiseSlider
                          min={1}
                          max={Math.max(1, userBalance)}
                          value={clampedOnlineBuyIn}
                          onChange={(v) => setOnlineBuyInDraft(Math.floor(Number(v) || 1))}
                          className="pb-0"
                        />
                        <button
                          type="button"
                          onClick={handleOnlineBuyIn}
                          className="w-full rounded-lg border border-neon-cyan/45 bg-neon-cyan/15 py-2.5 text-sm font-semibold text-neon-cyan transition hover:bg-neon-cyan/25"
                        >
                          Докупить {clampedOnlineBuyIn.toLocaleString('ru-RU')} SEKA
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={handleOnlineBuyIn}
                        className="w-full rounded-lg border border-neon-cyan/45 bg-neon-cyan/15 py-2.5 text-sm font-semibold text-neon-cyan transition hover:bg-neon-cyan/25"
                      >
                        Докупить {clampedOnlineBuyIn.toLocaleString('ru-RU')} SEKA
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="mt-3 text-center text-[11px] font-medium text-red-300/90">
                    Недостаточно средств на балансе для докупки.
                  </p>
                )}
              </div>
            ) : null}
            <div className="mx-auto mb-2 flex max-w-lg justify-end px-2">
              <button
                type="button"
                onClick={() => setShowMatchSessionModal(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/35 bg-amber-950/25 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-100/95 transition hover:bg-amber-950/40"
              >
                <LayoutList className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Матч
              </button>
            </div>
            {table.state === 'playing' && !gameState.gameEnded && canAct ? (
              showRaiseInput && raiseRange && clampedSekaRaise != null ? (
                <div className="mx-auto w-full max-w-lg px-2">
                  <PokerBetRaiseSlider
                    min={raiseRange.min}
                    max={raiseRange.max}
                    value={clampedSekaRaise}
                    onChange={setSekaRaiseAmount}
                    className="pb-2"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setShowRaiseInput(false)}
                      className="flex h-12 items-center justify-center rounded-lg border border-border bg-card/80 text-xs font-semibold text-foreground hover:bg-muted/40"
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePlayerMove('RAISE', clampedSekaRaise)}
                      className="flex h-12 items-center justify-center rounded-lg border border-neon-orange/50 bg-neon-orange/15 text-xs font-semibold text-neon-orange hover:bg-neon-orange/25"
                    >
                      Поднять до {clampedSekaRaise.toLocaleString('ru-RU')}
                    </button>
                  </div>
                </div>
              ) : (
                <SekaActionBar
                  actions={getAvailableActions()}
                  onAction={onBarAction}
                  disabled={false}
                />
              )
            ) : !showOnlineBuyInPanel && onlineIdleFooterCopy ? (
              <div className="mx-auto max-w-lg px-3 py-2 text-center">
                <p className="text-xs font-medium text-foreground">{onlineIdleFooterCopy.title}</p>
                {onlineIdleFooterCopy.detail ? (
                  <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{onlineIdleFooterCopy.detail}</p>
                ) : null}
              </div>
            ) : table.state === 'playing' && !gameState.gameEnded && !canAct && !showOnlineBuyInPanel ? (
              <p className="py-2 text-center text-xs text-muted-foreground">Ожидание хода…</p>
            ) : null}
            {!showRaiseInput ? (
              <div className="mx-auto mt-2 w-full max-w-lg px-1 sm:px-0">
                <button
                  type="button"
                  onClick={() => setLiveMovesOpen((o) => !o)}
                  aria-expanded={liveMovesOpen}
                  className="inline-flex w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-lg border border-white/12 bg-black/20 px-2 py-2 text-[10px] font-bold uppercase tracking-wide text-amber-100/95 transition hover:bg-black/30"
                >
                  {liveMovesOpen ? (
                    <ChevronUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                  Ходы раздачи
                  {handMoves.length > 0 ? (
                    <span className="rounded-full border border-amber-400/35 bg-amber-500/15 px-1.5 py-0 text-[9px] font-mono tabular-nums text-amber-50">
                      {handMoves.length}
                    </span>
                  ) : null}
                  {!liveMovesOpen && handMoves.length > 0 ? (
                    <LiveSekaHandMovesTrail
                      actions={handMoves}
                      className="min-w-0 flex-1 basis-full justify-center sm:basis-auto sm:flex-initial"
                    />
                  ) : null}
                </button>
                {liveMovesOpen ? (
                  <div className="mt-1.5 max-h-[min(40vh,280px)] overflow-y-auto rounded-lg border border-amber-500/25 bg-black/30 px-2 py-2 text-left">
                    <LiveSekaHandMovesPanel
                      actions={handMoves}
                      emptyHint="Ходы появятся после действий игроков на сервере."
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        {isSvarkaCommitOpen && (
          <div className="fixed inset-0 z-[70] overflow-y-auto overscroll-contain bg-black/55">
            <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
              <div className="pointer-events-auto w-full max-w-md min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto animate-[pulse_1.2s_ease-in-out_infinite] rounded-2xl border border-amber-400/70 bg-gradient-to-b from-amber-950/90 to-slate-950/95 p-4 shadow-[0_0_40px_rgba(251,191,36,0.35)]">
              <p className="text-center text-lg font-bold uppercase tracking-wide text-amber-100 drop-shadow-[0_0_14px_rgba(251,191,36,0.5)]">
                Свара ×{svarkaMultOnline}!
              </p>
              <p className="mt-2 text-center text-sm text-amber-100/90">
                Номинальный вход:{' '}
                <span className="font-mono font-semibold text-white">
                  {Number(svarkaCommit.effectiveAnte || 0).toLocaleString('ru-RU')} фишек
                </span>
              </p>
              {mySvarkaSeat?.canParticipate ? (
                <p className="mt-1 text-center text-[11px] text-amber-200/85">
                  С вашего стека спишется:{' '}
                  <span className="font-mono font-bold text-amber-50">
                    {Number(
                      mySvarkaSeat.entryAmount != null
                        ? mySvarkaSeat.entryAmount
                        : Math.min(Number(svarkaCommit.effectiveAnte || 0), userStack)
                    ).toLocaleString('ru-RU')}
                  </span>
                  {Number(
                    mySvarkaSeat.entryAmount != null
                      ? mySvarkaSeat.entryAmount
                      : Math.min(Number(svarkaCommit.effectiveAnte || 0), userStack)
                  ) < Number(svarkaCommit.effectiveAnte || 0)
                    ? ' (all-in к полному анте)'
                    : ''}
                </p>
              ) : null}
              <p className="mt-2 text-center text-[11px] text-amber-200/75">
                {svarkaCommit.phase === 'collecting'
                  ? `Без выбора через ${svarkaCommitSecondsLeft} с — пропуск раздачи`
                  : 'Решения приняты · показываем результаты…'}
              </p>
              {svarkaCommit.phase === 'collecting' ? (
                <div className="mt-2">
                  <div className="h-1.5 w-full overflow-hidden rounded-full border border-white/15 bg-black/40">
                    <div
                      className={cn(
                        'h-full rounded-full bg-gradient-to-r transition-[filter] duration-700 ease-linear',
                        svarkaCountdownShouldAnimateWidthOnline
                          ? 'transition-[width,filter] duration-700 ease-linear'
                          : 'transition-none',
                        svarkaCountdownLineClassOnline,
                        svarkaCommitSecondsLeft <= 3 && svarkaCommit.phase === 'collecting' && 'animate-pulse'
                      )}
                      style={{ width: svarkaCountdownWidthOnline }}
                    />
                  </div>
                </div>
              ) : null}

              <div className="mt-3 space-y-2">
                {svarkaCommit.seats.map((seat) => {
                  const tableSeat = table.players?.[seat.seatIndex];
                  const isSelf = String(seat.userId) === heroUserId || seat.seatIndex === currentPlayerIndex;
                  const effA = Number(svarkaCommit.effectiveAnte || 0);
                  const entryAmt =
                    seat.entryAmount != null
                      ? Number(seat.entryAmount)
                      : Math.min(effA, Number(tableSeat?.chips ?? 0));
                  return (
                    <div
                      key={`svarka-online-seat-${seat.seatIndex}`}
                      className="flex items-center justify-between rounded-lg border border-white/10 bg-black/25 px-2 py-1.5"
                    >
                      <div className="flex items-center gap-2">
                        <OnlineSeatAvatarBubble
                          isBot={Boolean(tableSeat?.isBot)}
                          displayName={isSelf ? 'Вы' : seat.username}
                          avatarUrl={String(
                            seat.avatarUrl ||
                              tableSeat?.avatarUrl ||
                              (isSelf ? user?.avatarUrl : '') ||
                              ''
                          ).trim()}
                          frameClassName="h-7 w-7"
                          initialsClassName="text-[10px]"
                        />
                        <div className="text-left">
                          <p className="text-[11px] font-semibold text-white">
                            {isSelf ? 'Вы' : seat.username}
                          </p>
                          <p className="text-[10px] text-amber-200/70">
                            {seat.canParticipate
                              ? `вход до ${entryAmt.toLocaleString('ru-RU')}${entryAmt < effA ? ' · all-in' : ''}`
                              : 'нет фишек на столе'}
                          </p>
                        </div>
                      </div>
                      {svarkaCommit.phase === 'collecting' ? (
                        seat.chose ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/40 bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-100">
                            <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
                            Выбор сделан
                          </span>
                        ) : (
                          <span className="rounded-full border border-white/20 bg-white/5 px-2 py-0.5 text-[10px] text-muted-foreground">
                            Ожидание…
                          </span>
                        )
                      ) : seat.participate ? (
                        <span className="rounded-full border border-emerald-300/45 bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-100">
                          В игре
                        </span>
                      ) : (
                        <span className="rounded-full border border-red-300/45 bg-red-500/20 px-2 py-0.5 text-[10px] font-semibold text-red-100">
                          Пас
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                {heroCanChooseSvarkaOnline ? (
                  <>
                    <button
                      type="button"
                      onClick={() => handleSvarkaChoice(false)}
                      disabled={svarkaCommit.phase !== 'collecting' || svarkaChoiceSent || Boolean(mySvarkaSeat?.chose)}
                      className="flex min-h-[2.75rem] items-center justify-center rounded-lg border border-border bg-card/80 px-2 text-center text-[10px] font-semibold uppercase text-foreground hover:bg-muted/40 sm:text-[11px] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Пропустить раздачу
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSvarkaChoice(true)}
                      disabled={
                        svarkaCommit.phase !== 'collecting' ||
                        svarkaChoiceSent ||
                        Boolean(mySvarkaSeat?.chose) ||
                        !Boolean(mySvarkaSeat?.canParticipate)
                      }
                      className="flex min-h-[2.75rem] items-center justify-center rounded-lg border border-amber-400/55 bg-amber-500/20 px-2 text-center text-[10px] font-semibold uppercase text-amber-50 hover:bg-amber-500/30 sm:text-[11px] disabled:cursor-not-allowed disabled:border-border/50 disabled:bg-card/50 disabled:text-muted-foreground"
                    >
                      {svarkaChoiceSent || Boolean(mySvarkaSeat?.chose)
                        ? 'Выбор принят'
                        : Number(
                            mySvarkaSeat?.entryAmount != null
                              ? mySvarkaSeat.entryAmount
                              : Math.min(Number(svarkaCommit.effectiveAnte || 0), userStack)
                          ) < Number(svarkaCommit.effectiveAnte || 0)
                          ? 'Участвовать (All-in)'
                          : 'Участвовать'}
                    </button>
                  </>
                ) : (
                  <p className="col-span-2 rounded-lg border border-white/15 bg-black/20 px-2 py-2 text-center text-[11px] text-amber-100/85">
                    Вы в режиме наблюдения — выбор в сваре недоступен.
                  </p>
                )}
              </div>
            </div>
          </div>
          </div>
        )}
      </main>

      {showMatchSessionModal ? (
        <div
          className="fixed inset-0 z-[73] flex items-end justify-center overflow-y-auto overscroll-contain bg-black/65 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="match-session-title"
          onClick={() => setShowMatchSessionModal(false)}
        >
          <div
            className="pointer-events-auto relative w-full max-w-lg rounded-2xl border border-amber-500/25 bg-gradient-to-b from-[#14161f]/98 to-background/98 p-4 shadow-[0_0_52px_rgba(0,0,0,0.5)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowMatchSessionModal(false)}
              className="absolute right-2 top-2 rounded-lg border border-white/10 bg-black/25 p-1.5 text-muted-foreground transition hover:bg-black/40 hover:text-foreground"
              aria-label="Закрыть"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
            <div className="flex items-start gap-2 pr-10">
              <LayoutList className="mt-0.5 h-5 w-5 shrink-0 text-amber-200/90" aria-hidden />
              <div>
                <h2 id="match-session-title" className="text-sm font-bold uppercase tracking-wide text-foreground">
                  Матч за столом
                </h2>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{table.name}</p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-lg border border-white/10 bg-black/25 px-2 py-2 text-center">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Раздач</p>
                <p className="font-mono text-lg font-bold tabular-nums text-foreground">{sessionTotalHands}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/25 px-2 py-2 text-center">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Побед</p>
                <p className="font-mono text-lg font-bold tabular-nums text-amber-200/95">{sessionHeroWins}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/25 px-2 py-2 text-center">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Пораж.</p>
                <p className="font-mono text-lg font-bold tabular-nums text-rose-200/90">{sessionHeroLosses}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/25 px-2 py-2 text-center">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">В архиве</p>
                <p className="font-mono text-lg font-bold tabular-nums text-foreground">{effectiveChronicleHands.length}</p>
              </div>
            </div>
            {blindsLabel !== '—' || table.buyIn != null ? (
              <p className="mt-2 text-center text-[10px] text-muted-foreground">
                {blindsLabel !== '—' ? `Лимиты: ${blindsLabel}` : ''}
                {blindsLabel !== '—' && table.buyIn != null ? ' · ' : ''}
                {table.buyIn != null ? `Вход ${Number(table.buyIn).toLocaleString('ru-RU')} SEKA` : ''}
              </p>
            ) : null}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-white/8 pt-3">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Фильтр списков
              </span>
              <label className="inline-flex cursor-pointer items-center gap-2 text-[10px] text-amber-100/90">
                <input
                  type="checkbox"
                  checked={matchSessionChronicleHeroOnly}
                  disabled={!heroUserId}
                  onChange={(e) => setMatchSessionChronicleHeroOnly(e.target.checked)}
                  className="rounded border-white/20 bg-black/40"
                />
                Только раздачи с моим участием
              </label>
            </div>
            {!heroUserId ? (
              <p className="mt-1 text-[10px] text-muted-foreground">
                Войдите, чтобы отфильтровать хронику и бухгалтерию по своему месту.
              </p>
            ) : null}
            {serverCompletedHandSummaries.length > 0 ? (
              <div className="mt-3 border-t border-emerald-500/25 pt-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-200/90">
                  Бухгалтерия по раздачам (сервер)
                </p>
                <p className="mt-1 text-[9px] text-muted-foreground">
                  Стеки до и после вскрытия; дельта по каждому месту.
                </p>
                <div className="mt-2 max-h-[min(36vh,240px)] space-y-3 overflow-y-auto pr-1">
                  {serverCompletedHandSummariesFiltered.length === 0 ? (
                    <p className="py-4 text-center text-[11px] text-muted-foreground">
                      {matchSessionChronicleHeroOnly
                        ? 'Нет раздач с вашим участием в серверной бухгалтерии.'
                        : 'Нет данных.'}
                    </p>
                  ) : (
                    serverCompletedHandSummariesFiltered.map((h) => (
                    <div
                      key={`srv-hand-${h.handSeq}-${h.recordedAt}`}
                      className="rounded-lg border border-white/10 bg-black/35 px-2 py-2 text-left"
                    >
                      <p className="text-[11px] font-semibold text-foreground">
                        Раздача №{h.handSeq}{' '}
                        <span className="font-mono text-amber-200/95">
                          · банк {Number(h.pot || 0).toLocaleString('ru-RU')}
                        </span>
                        {h.earlyFinish ? (
                          <span className="ml-1 rounded border border-amber-500/35 bg-amber-950/40 px-1.5 py-0 text-[8px] font-bold uppercase tracking-wide text-amber-100/90">
                            раннее завершение
                          </span>
                        ) : null}
                      </p>
                      {Array.isArray(h.winners) && h.winners.length > 0 ? (
                        <p className="mt-0.5 text-[10px] text-muted-foreground">
                          Победители:{' '}
                          {h.winners.map((w) => w.username || w.userId).filter(Boolean).join(', ')}
                        </p>
                      ) : null}
                      {(Number(h.potGross || 0) > 0 || Number(h.rakeAmount || 0) > 0 || Number(h.potNet || 0) > 0) ? (
                        <p className="mt-0.5 text-[10px] text-muted-foreground">
                          Брутто/комиссия/нетто: {Number(h.potGross || 0).toLocaleString('ru-RU')} /{' '}
                          {Number(h.rakeAmount || 0).toLocaleString('ru-RU')} / {Number(h.potNet || 0).toLocaleString('ru-RU')} SEKA
                        </p>
                      ) : null}
                      <div className="mt-1.5 overflow-x-auto">
                        <table className="w-full min-w-[280px] border-collapse text-[9px]">
                          <thead>
                            <tr className="text-left text-muted-foreground">
                              <th className="py-0.5 pr-1 font-medium">#</th>
                              <th className="py-0.5 pr-1 font-medium">Игрок</th>
                              <th className="py-0.5 pr-1 font-mono font-medium">Было</th>
                              <th className="py-0.5 pr-1 font-mono font-medium">Стало</th>
                              <th className="py-0.5 font-mono font-medium">Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(Array.isArray(h.seats) ? h.seats : []).map((s) => (
                              <tr key={`srv-seat-${h.handSeq}-${s.seatIndex}`} className="border-t border-white/5">
                                <td className="py-0.5 pr-1 tabular-nums text-muted-foreground">{s.seatIndex}</td>
                                <td className="max-w-[120px] truncate py-0.5 pr-1 text-foreground">
                                  {s.username || '—'}
                                </td>
                                <td className="py-0.5 pr-1 font-mono tabular-nums">
                                  {Number(s.chipsBefore || 0).toLocaleString('ru-RU')}
                                </td>
                                <td className="py-0.5 pr-1 font-mono tabular-nums">
                                  {Number(s.chipsAfter || 0).toLocaleString('ru-RU')}
                                </td>
                                <td
                                  className={cn(
                                    'py-0.5 font-mono tabular-nums',
                                    Number(s.delta) > 0
                                      ? 'text-emerald-300/95'
                                      : Number(s.delta) < 0
                                        ? 'text-rose-300/90'
                                        : 'text-muted-foreground'
                                  )}
                                >
                                  {Number(s.delta) > 0 ? '+' : ''}
                                  {Number(s.delta || 0).toLocaleString('ru-RU')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    ))
                  )}
                </div>
              </div>
            ) : null}
            {matchSessionFullHandRows.length > 0 ? (
              <div className="mt-3 border-t border-amber-400/30 pt-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-amber-200/95">
                  Полная сводка по раздачам
                </p>
                <p className="mt-1 text-[9px] leading-snug text-muted-foreground">
                  Карты и комбинации на финише, дельты по стеку и ходы — как в локальной истории матча.
                </p>
                <div className="mt-2 max-h-[min(52vh,360px)] space-y-2 overflow-y-auto pr-1">
                  {matchSessionFullHandRowsFiltered.length === 0 ? (
                    <p className="py-4 text-center text-[11px] text-muted-foreground">
                      {matchSessionChronicleHeroOnly
                        ? 'По фильтру нет раздач с вашим участием в полной сводке.'
                        : 'Нет данных.'}
                    </p>
                  ) : (
                    matchSessionFullHandRowsFiltered.map((row) => (
                      <div
                        key={row.id}
                        className="rounded-xl border border-white/10 bg-black/25 px-3 py-2 text-left"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-[11px] font-bold text-amber-100">
                            Раздача №{row.handSeq} · Банк {row.pot.toLocaleString('ru-RU')} SEKA
                          </p>
                          <span className="rounded border border-white/15 bg-black/25 px-1.5 py-0.5 text-[10px] text-amber-200">
                            {row.endType}
                          </span>
                        </div>
                        <p className="mt-1 text-[10px] text-amber-100/90">Победитель: {row.winnerNames}</p>
                        {heroUserId ? (
                          <p className="mt-1 text-[11px]">
                            Ваш итог:{' '}
                            <span
                              className={cn(
                                'font-mono font-black',
                                row.heroDelta > 0 && 'text-emerald-200',
                                row.heroDelta < 0 && 'text-red-200',
                                row.heroDelta === 0 && 'text-white/80'
                              )}
                            >
                              {row.heroDelta > 0 ? '+' : ''}
                              {row.heroDelta.toLocaleString('ru-RU')} SEKA
                            </span>
                          </p>
                        ) : null}
                        {row.timestamp ? (
                          <p className="mt-0.5 text-[10px] text-amber-200/70">
                            {row.timestamp.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        ) : null}
                        {row.playerSnapshots.length > 0 ? (
                          <div className="mt-2 rounded-lg border border-cyan-500/25 bg-black/25 px-2 py-1.5">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-cyan-100/90">
                              Карты и комбинации
                            </p>
                            <div className="mt-2 space-y-2">
                              {row.playerSnapshots.map((ps) => (
                                <div
                                  key={ps.key}
                                  className={cn(
                                    'rounded-lg border px-2 py-1.5',
                                    ps.isWinner
                                      ? 'border-emerald-500/45 bg-emerald-950/35'
                                      : 'border-white/10 bg-black/35'
                                  )}
                                >
                                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                    <span className="text-[10px] font-bold text-white/95">
                                      {ps.isHero ? 'Вы · ' : null}
                                      {ps.name}
                                      <span className="ml-1 font-mono text-[9px] font-normal text-white/45">
                                        м.{ps.seatIndex + 1}
                                      </span>
                                    </span>
                                    {ps.isWinner ? (
                                      <span className="rounded bg-emerald-500/25 px-1 py-0 text-[9px] font-bold uppercase text-emerald-100">
                                        победа
                                      </span>
                                    ) : null}
                                    {ps.isFolded ? (
                                      <span className="rounded bg-white/10 px-1 py-0 text-[9px] text-white/65">
                                        сброс
                                      </span>
                                    ) : null}
                                  </div>
                                  {ps.handDescription ? (
                                    <p className="mt-0.5 text-[9px] leading-snug text-amber-200/90">
                                      {ps.handDescription}
                                    </p>
                                  ) : null}
                                  <div className="mt-1 flex flex-wrap justify-center gap-0.5">
                                    {ps.cardsUi.length > 0 ? (
                                      ps.cardsUi.map((c, ci) => (
                                        <PlayingCard
                                          key={`${ps.key}-c${ci}`}
                                          suit={c.suit}
                                          rank={c.rank}
                                          size="sm"
                                        />
                                      ))
                                    ) : (
                                      <span className="text-[9px] text-white/45">нет карт в записи</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        <div className="mt-2 rounded-lg border border-white/10 bg-black/20 px-2 py-1.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-100/85">
                            Баланс за раздачу
                          </p>
                          <div className="mt-1 grid gap-1 sm:grid-cols-2">
                            {row.deltas.map((d) => (
                              <div
                                key={`${row.id}-d-${d.playerId}`}
                                className="flex items-center justify-between gap-2 rounded bg-black/25 px-2 py-1"
                              >
                                <span className="truncate text-[10px] text-white/90">{d.playerName}</span>
                                <span
                                  className={cn(
                                    'font-mono text-[10px] font-bold',
                                    d.delta > 0 && 'text-emerald-200',
                                    d.delta < 0 && 'text-red-200',
                                    d.delta === 0 && 'text-white/75'
                                  )}
                                >
                                  {d.delta > 0 ? '+' : ''}
                                  {Number(d.delta || 0).toLocaleString('ru-RU')} SEKA
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="mt-2 rounded-lg border border-white/10 bg-black/20 px-2 py-1.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-100/85">Ходы</p>
                          {row.actions.length > 0 ? (
                            <>
                              <MatchChronicleChain
                                steps={row.actions.map(
                                  (a, actionIdx) =>
                                    `${actionIdx + 1}. ${a.playerName || '—'}: ${formatHistoryAction(a)}`
                                )}
                              />
                              <ol className="mt-2 space-y-1 border-t border-white/5 pt-2">
                                {row.actions.map((a, actionIdx) => (
                                  <li
                                    key={`${row.id}-a-${actionIdx}`}
                                    className="flex items-center justify-between gap-2 rounded bg-black/25 px-2 py-1 text-[10px]"
                                  >
                                    <span className="truncate text-white/90">
                                      {actionIdx + 1}. {a.playerName || '—'}
                                    </span>
                                    <span className="text-amber-100">{formatHistoryAction(a)}</span>
                                  </li>
                                ))}
                              </ol>
                            </>
                          ) : (
                            <p className="mt-1 text-[10px] text-white/70">
                              Действия не записаны (авто-завершение или ранний выход).
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : null}
            <div className="mt-3 border-t border-white/8 pt-3">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Хроника завершённых раздач (клиент)
              </span>
              {matchSessionFullHandRows.length > 0 ? (
                <p className="mt-1 text-[9px] text-muted-foreground">
                  Краткий текстовый архив ходов; детали см. выше в полной сводке.
                </p>
              ) : null}
            </div>
            <div className="mt-2 max-h-[min(42vh,280px)] overflow-y-auto rounded-lg border border-amber-500/20 bg-black/30 px-2 py-2">
              {matchChronicleFlatStepsForModal.length > 0 ? (
                <MatchChronicleVertical steps={matchChronicleFlatStepsForModal} />
              ) : (
                <p className="py-6 text-center text-[11px] text-muted-foreground">
                  {matchSessionChronicleHeroOnly
                    ? 'Нет раздач с вашим участием в клиентском архиве.'
                    : effectiveChronicleHands.length === 0
                      ? 'Завершённые раздачи появятся здесь после следующей руки.'
                      : 'Нет данных для отображения.'}
                </p>
              )}
            </div>
            {chronicleSteps.length > 0 ? (
              <div className="mt-3 border-t border-white/8 pt-3">
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-amber-200/85">
                  Текущая раздача
                  {Number(gameState.handSeq) > 0 ? (
                    <span className="ml-1 font-mono font-normal text-muted-foreground">№{gameState.handSeq}</span>
                  ) : null}
                </p>
                <MatchChronicleChain steps={chronicleSteps} />
              </div>
            ) : null}
            <p className="mt-3 text-[9px] leading-snug text-muted-foreground">
              «Копировать полностью» — статистика, бухгалтерия с сервера и хроника. «Копировать хронику» — только текст ходов
              (архив и текущая раздача). При включённом фильтре в оба варианта попадают только отфильтрованные раздачи.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <button
                type="button"
                onClick={() => void handleCopyFullSession()}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-400/40 bg-amber-950/35 px-3 py-2 text-xs font-semibold text-amber-50 transition hover:bg-amber-950/50"
              >
                <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {matchSessionFullCopyFeedback ? 'Сводка скопирована' : 'Копировать полностью'}
              </button>
              <button
                type="button"
                onClick={() => void handleCopyMatchChronicle()}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-xs font-semibold text-foreground transition hover:bg-black/45"
              >
                <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Копировать хронику
              </button>
              <button
                type="button"
                onClick={() => setShowMatchSessionModal(false)}
                className="rounded-lg border border-neon-cyan/40 bg-neon-cyan/15 px-3 py-2 text-xs font-semibold text-neon-cyan transition hover:bg-neon-cyan/25"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showHandOutcomeModal && gameState.gameEnded ? (
        <div
          className="fixed inset-0 z-[72] overflow-y-auto overscroll-contain bg-black/70 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="hand-outcome-title"
        >
          <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
            <div
              className={cn(
                'pointer-events-auto relative isolate w-full max-w-[min(94vw,560px)] min-h-0 overflow-y-auto rounded-2xl px-4 py-4 text-center text-slate-100 sm:px-6 sm:py-5',
                'max-h-[min(85dvh,calc(100dvh-1.5rem))]',
                outcomeKind === 'win' &&
                  cn(
                    !prefersReducedMotion && 'poker-fx-win-panel',
                    'border border-emerald-400/55 bg-gradient-to-b from-emerald-950/95 to-[#0a1520]/98 shadow-[0_0_60px_rgba(52,211,153,0.25)]'
                  ),
                outcomeKind === 'lose' &&
                  cn(
                    !prefersReducedMotion && 'poker-fx-loss-panel',
                    'border border-red-500/50 bg-gradient-to-b from-red-950/92 to-[#0a1520]/98 shadow-[0_0_40px_rgba(248,113,113,0.22)]'
                  ),
                outcomeKind === 'chop' &&
                  'border border-amber-400/50 bg-gradient-to-b from-amber-950/92 to-[#0a1520]/98 shadow-lg',
                !outcomeKind &&
                  'border border-amber-400/35 bg-gradient-to-b from-[#161a26]/98 to-[#0b0e14]/98 shadow-[0_0_48px_rgba(0,0,0,0.55)]'
              )}
            >
              {outcomeKind === 'win' && (
                <>
                  <Trophy className="mx-auto mb-1.5 h-9 w-9 text-emerald-300 sm:h-10 sm:w-10" aria-hidden />
                  <p id="hand-outcome-title" className="text-lg font-black uppercase tracking-wide text-emerald-100 sm:text-xl">
                    Победа
                  </p>
                </>
              )}
              {outcomeKind === 'lose' && (
                <>
                  <Skull className="mx-auto mb-1.5 h-9 w-9 text-red-300 sm:h-10 sm:w-10" aria-hidden />
                  <p id="hand-outcome-title" className="text-lg font-black uppercase tracking-wide text-red-100 sm:text-xl">
                    Поражение
                  </p>
                </>
              )}
              {outcomeKind === 'chop' && (
                <>
                  <Sparkles className="mx-auto mb-1.5 h-9 w-9 text-amber-200 sm:h-10 sm:w-10" aria-hidden />
                  <p id="hand-outcome-title" className="text-lg font-black uppercase tracking-wide text-amber-100 sm:text-xl">
                    Ничья
                  </p>
                </>
              )}
              {!outcomeKind ? (
                <p
                  id="hand-outcome-title"
                  className="text-lg font-black uppercase tracking-wide text-amber-50 drop-shadow-[0_0_12px_rgba(251,191,36,0.35)] sm:text-xl"
                >
                  Итог раздачи
                </p>
              ) : null}

              {heroGainSeka != null && heroGainSeka > 0 ? (
                <div
                  className={cn(
                    'mx-auto mt-3 w-full max-w-md overflow-hidden rounded-2xl border px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]',
                    outcomeKind === 'chop'
                      ? 'border-amber-400/45 bg-gradient-to-b from-amber-400/16 via-amber-950/50 to-[#0a0806]/95 shadow-[0_0_36px_rgba(251,191,36,0.18)]'
                      : 'border-emerald-400/40 bg-gradient-to-b from-emerald-400/18 via-emerald-950/55 to-[#050a08]/95 shadow-[0_0_40px_rgba(52,211,153,0.22)]'
                  )}
                >
                  <div className="flex flex-col items-center text-center">
                    <div
                      className={cn(
                        'flex h-12 w-12 items-center justify-center rounded-full border shadow-[0_0_20px_rgba(52,211,153,0.35)]',
                        outcomeKind === 'chop'
                          ? 'border-amber-300/50 bg-amber-500/20 shadow-[0_0_18px_rgba(251,191,36,0.3)]'
                          : 'border-emerald-300/50 bg-emerald-500/20'
                      )}
                    >
                      <TrendingUp
                        className={cn(
                          'h-6 w-6',
                          outcomeKind === 'chop' ? 'text-amber-200' : 'text-emerald-200'
                        )}
                        aria-hidden
                      />
                    </div>
                    <p
                      className={cn(
                        'mt-2 text-[10px] font-black uppercase tracking-[0.2em]',
                        outcomeKind === 'chop' ? 'text-amber-200/90' : 'text-emerald-200/90'
                      )}
                    >
                      Фишки пришли
                    </p>
                    <p
                      className={cn(
                        'mt-1 font-mono text-[clamp(1.75rem,6vw,2.75rem)] font-black leading-none tabular-nums tracking-tight',
                        outcomeKind === 'chop'
                          ? 'text-amber-50 [text-shadow:0_0_22px_rgba(251,191,36,0.35)]'
                          : 'text-emerald-50 [text-shadow:0_0_24px_rgba(167,243,208,0.45)]'
                      )}
                    >
                      +{heroGainSeka.toLocaleString('ru-RU')}
                      <span
                        className={cn(
                          'ml-1.5 align-baseline text-lg font-bold',
                          outcomeKind === 'chop' ? 'text-amber-200/95' : 'text-emerald-200/95'
                        )}
                      >
                        SEKA
                      </span>
                    </p>
                    <p
                      className={cn(
                        'mt-2 max-w-[280px] text-[11px] leading-snug',
                        outcomeKind === 'chop' ? 'text-amber-100/85' : 'text-emerald-100/85'
                      )}
                    >
                      Ровно столько добавилось к стеку за эту раздачу. Баланс в кошельке обновится после синка с
                      сервером.
                    </p>
                  </div>
                </div>
              ) : null}

              {showNextHandVoteInOutcomeModal ? (
                <div className="mx-auto mt-4 w-full max-w-md rounded-xl border border-neon-cyan/40 bg-black/50 px-3 py-4 text-left shadow-inner">
                  <p className="text-center text-sm font-bold uppercase tracking-wide text-cyan-100">
                    Следующая раздача
                  </p>
                  <p className="mt-1.5 text-center text-[11px] leading-snug text-slate-300">
                    У каждого есть{' '}
                    <span className="font-mono font-bold text-amber-200">{Math.round(nextHandBudgetMs / 1000)}</span> с,
                    чтобы нажать «Готов». Кто не успеет —{' '}
                    <span className="font-semibold text-rose-200/95">покинет стол</span> (исключение из комнаты).
                  </p>
                  <p className="mt-2 text-center text-[11px] font-semibold text-slate-200">
                    Проголосовало:{' '}
                    <span className="font-mono text-cyan-100">
                      {nextHandReadyCount} / {nextHandVoteTotal}
                    </span>
                  </p>
                  {nextHandDeadlineAtMs ? (
                    <div className="mt-3">
                      <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-wide text-slate-400">
                        <span>Таймаут</span>
                        <span
                          className={cn(
                            'font-mono text-sm font-black tabular-nums text-cyan-100',
                            nextHandRemainingMs <= 8000 && 'text-rose-300',
                            nextHandRemainingMs <= 4000 && 'animate-pulse'
                          )}
                        >
                          {Math.max(0, Math.ceil(nextHandRemainingMs / 1000))} с
                        </span>
                      </div>
                      <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full border border-white/15 bg-black/55">
                        <div
                          className={cn(
                            'h-full rounded-full bg-gradient-to-r transition-[width] duration-300 ease-linear',
                            nextHandRemainingMs <= 8000
                              ? 'from-rose-500 via-orange-400 to-amber-300'
                              : 'from-emerald-500 via-teal-400 to-cyan-400'
                          )}
                          style={{ width: `${Math.round(nextHandCountdownRatio * 1000) / 10}%` }}
                        />
                      </div>
                    </div>
                  ) : null}
                  {heroUserId ? (
                    <button
                      type="button"
                      onClick={handleReadyNextHand}
                      disabled={heroReadyForNextHand}
                      className={cn(
                        'mt-4 flex min-h-[3rem] w-full items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-black uppercase tracking-wide transition',
                        heroReadyForNextHand
                          ? 'cursor-default border-emerald-400/55 bg-emerald-500/18 text-emerald-100'
                          : 'border-neon-cyan/55 bg-neon-cyan/18 text-neon-cyan hover:bg-neon-cyan/28'
                      )}
                    >
                      <Check className="h-5 w-5 shrink-0" aria-hidden />
                      {heroReadyForNextHand ? 'Вы отметились готовым' : 'Готов к следующей раздаче'}
                    </button>
                  ) : (
                    <p className="mt-3 text-center text-[11px] text-amber-200/85">Войдите в аккаунт, чтобы голосовать.</p>
                  )}
                  {canOfferGraceExitBetweenHands ? (
                    <div className="mt-4 rounded-xl border border-emerald-500/35 bg-emerald-950/30 px-3 py-3 text-left shadow-inner">
                      {graceExitHeroInProfit ? (
                        <p className="text-center text-[10px] font-semibold leading-snug text-emerald-100/90">
                          Забрать выигрыш за столом: чистый плюс{' '}
                          <span className="font-mono font-bold text-emerald-200">
                            +{Number(heroGraceChipNet).toLocaleString('ru-RU')} SEKA
                          </span>{' '}
                          — бонус респекта для всех за столом, затем выход в главное лобби после окна награды.
                        </p>
                      ) : (
                        <p className="text-center text-[10px] font-semibold leading-snug text-emerald-100/90">
                          Уйти между раздачами: бонус респекта по PnL для всех за столом (включая вас). Чистый итог по
                          фишкам{' '}
                          <span className="font-mono font-bold text-emerald-200">
                            {Number(heroGraceChipNet) >= 0 ? '' : '−'}
                            {Math.abs(Number(heroGraceChipNet)).toLocaleString('ru-RU')} SEKA
                          </span>
                          . Затем выход в главное лобби после окна награды.
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={handleGraceExitLeave}
                        className="mt-3 flex w-full min-h-[2.85rem] items-center justify-center gap-2 rounded-lg border-2 border-emerald-600/55 bg-gradient-to-b from-emerald-500/25 via-emerald-800/30 to-emerald-950/85 px-3 py-2.5 text-sm font-black uppercase tracking-wide text-emerald-50 shadow-[0_4px_0_rgb(8,47,32),0_12px_28px_rgba(52,211,153,0.2)] transition hover:brightness-110 active:translate-y-0.5 active:shadow-[0_2px_0_rgb(8,47,32)]"
                      >
                        {graceExitHeroInProfit ? 'Забрать выигрыш и уйти' : 'Принять поражение и уйти'}
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {returnView === 'online-lobby' && !showNextHandVoteInOutcomeModal ? (
                <div className="mx-auto mt-4 w-full max-w-md rounded-xl border border-orange-500/40 bg-gradient-to-b from-orange-950/50 via-[#120f0c]/95 to-black/60 px-3 py-3 shadow-[inset_0_1px_0_rgba(251,146,60,0.14)]">
                  <p className="text-center text-[10px] font-semibold leading-snug text-orange-100/90">
                    Без ожидания следующей раздачи: выход с матча со штрафом по фишкам — откроется подтверждение.
                  </p>
                  <button
                    type="button"
                    onClick={handleHeaderLeave}
                    className="mt-3 flex w-full min-h-[2.85rem] items-center justify-center gap-2 rounded-lg border-2 border-orange-600/60 bg-gradient-to-b from-orange-400/20 via-orange-800/35 to-orange-950/80 px-3 py-2.5 text-sm font-black uppercase tracking-wide text-orange-50 shadow-[0_4px_0_rgb(67,24,8),0_12px_28px_rgba(234,88,12,0.22)] transition hover:brightness-110 active:translate-y-0.5 active:shadow-[0_2px_0_rgb(67,24,8)]"
                  >
                    <Skull className="h-5 w-5 shrink-0 text-orange-100" aria-hidden />
                    Сбежать с корабля
                  </button>
                </div>
              ) : null}

              <div className="mx-auto mt-4 w-full max-w-md rounded-xl border border-white/18 bg-black/50 px-3 py-3 text-left text-[11px] leading-relaxed text-slate-100 shadow-inner">
                <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
                  <p className="font-mono text-[12px] font-bold tabular-nums text-amber-200">
                    Банк: {Number(gameState.pot || 0).toLocaleString('ru-RU')} SEKA
                  </p>
                  {Number(gameState.handSeq) > 0 ? (
                    <p className="font-mono text-[10px] text-slate-400">Раздача №{Number(gameState.handSeq)}</p>
                  ) : null}
                </div>
                <p className="mt-1.5 text-[12px] text-slate-100">{outcomeWinnerSummary || 'Итог без победителя'}</p>

                {outcomeAllSeatsRows.length > 0 ? (
                  <ul className="mt-3 space-y-1 border-t border-white/10 pt-2">
                    {outcomeAllSeatsRows.map((row) => {
                      const voted = nextHandReadyIdSet.has(row.key);
                      const dn = row.deltaNum;
                      const tpSeat = table?.players?.[row.seatIndex];
                      const seatBot = Boolean(tpSeat?.isBot);
                      const seatAv = String(
                        row.avatarUrl ||
                          tpSeat?.avatarUrl ||
                          (String(row.key) === String(heroUserId) ? user?.avatarUrl : '') ||
                          ''
                      ).trim();
                      return (
                        <li
                          key={row.key}
                          className="flex flex-wrap items-center gap-2 rounded-lg border border-white/12 bg-black/35 px-2 py-1.5 text-[11px]"
                        >
                          <OnlineSeatAvatarBubble
                            isBot={seatBot}
                            displayName={row.name}
                            avatarUrl={seatAv}
                            frameClassName="h-8 w-8"
                            initialsClassName="text-[9px]"
                          />
                          <span className="min-w-0 flex-1 truncate font-medium text-slate-100">
                            <span className="font-mono text-[9px] text-slate-500">м.{row.seatIndex + 1}</span>{' '}
                            {row.name}
                            {String(row.key) === String(heroUserId) ? (
                              <span className="ml-1 text-[9px] font-normal text-cyan-200">(вы)</span>
                            ) : null}
                          </span>
                          {dn != null && Number.isFinite(dn) ? (
                            <span
                              className={cn(
                                'shrink-0 font-mono text-[10px] font-bold tabular-nums',
                                dn > 0 && 'text-emerald-300',
                                dn < 0 && 'text-red-300',
                                dn === 0 && 'text-slate-500'
                              )}
                            >
                              {dn > 0 ? '+' : ''}
                              {dn.toLocaleString('ru-RU')}
                            </span>
                          ) : (
                            <span className="shrink-0 text-[9px] text-slate-500">—</span>
                          )}
                          <span className="shrink-0 font-mono text-[9px] tabular-nums text-slate-500">
                            → {row.chipsAfter.toLocaleString('ru-RU')}
                          </span>
                          {showNextHandVoteInOutcomeModal ? (
                            <span
                              className={cn(
                                'ml-auto shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase',
                                voted ? 'bg-emerald-500/30 text-emerald-100' : 'bg-white/12 text-slate-400'
                              )}
                            >
                              {voted ? 'Готов' : 'Ждём'}
                            </span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : outcomeSortedDeltas.length > 0 ? (
                  <ul className="mt-3 space-y-1 border-t border-white/10 pt-2">
                    {outcomeSortedDeltas.map((d, idx) => (
                      <li
                        key={`${d.playerId || d.playerName || 'p'}-${idx}`}
                        className="flex items-center gap-2 rounded-lg border border-white/12 bg-black/35 px-2 py-1.5 text-[11px]"
                      >
                        <span className="min-w-0 flex-1 truncate font-medium text-slate-100">
                          {d.playerName || '—'}
                        </span>
                        <span
                          className={cn(
                            'shrink-0 font-mono text-[10px] font-bold tabular-nums',
                            d.deltaNum > 0 && 'text-emerald-300',
                            d.deltaNum < 0 && 'text-red-300',
                            d.deltaNum === 0 && 'text-slate-500'
                          )}
                        >
                          {d.deltaNum > 0 ? '+' : ''}
                          {d.deltaNum.toLocaleString('ru-RU')}
                        </span>
                        <span className="shrink-0 font-mono text-[9px] tabular-nums text-slate-500">
                          → {d.endNum.toLocaleString('ru-RU')}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              {outcomeAllSeatsRows.length === 0 && outcomeHeroDelta != null ? (
                <>
                  <p className="mx-auto mt-3 max-w-md px-1 text-center text-[11px] leading-snug text-slate-300">
                    Изменение вашего стека за эту раздачу — с учётом анте и торгов.
                  </p>
                  <p
                    className={cn(
                      'mx-auto mt-2 max-w-md text-center font-mono text-xl font-black tabular-nums tracking-tight sm:text-2xl',
                      outcomeHeroDelta > 0 && 'text-emerald-300',
                      outcomeHeroDelta < 0 && 'text-red-300',
                      outcomeHeroDelta === 0 && 'text-amber-200'
                    )}
                  >
                    {outcomeHeroDelta > 0 ? '+' : ''}
                    {Number(outcomeHeroDelta).toLocaleString('ru-RU')} SEKA
                  </p>
                </>
              ) : null}
              <div className="mt-5 flex w-full flex-wrap items-center justify-center gap-2 sm:justify-end">
                <button
                  type="button"
                  onClick={() => void handleCopyHandOutcome()}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[10px] font-bold transition',
                    handOutcomeCopyFeedback
                      ? 'border-emerald-400/60 bg-emerald-500/20 text-emerald-100'
                      : outcomeKind === 'lose'
                        ? 'border-red-400/45 bg-red-500/12 text-red-100 hover:bg-red-500/22'
                        : outcomeKind === 'win'
                          ? 'border-emerald-400/45 bg-emerald-500/12 text-emerald-100 hover:bg-emerald-500/22'
                          : 'border-amber-400/45 bg-amber-500/12 text-amber-100 hover:bg-amber-500/22'
                  )}
                >
                  <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {handOutcomeCopyFeedback ? 'Снимок скопирован' : 'Копировать снимок (хроника + раздача)'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setHandOutcomeDetailsExpanded(false);
                    setShowHandOutcomeModal(false);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-black/30 px-2.5 py-1.5 text-[10px] font-bold text-amber-100 transition hover:bg-black/45"
                >
                  <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Закрыть
                </button>
              </div>
              {(outcomeSnapshots.length > 0 || outcomeDetailMoves.length > 0) && (
                <button
                  type="button"
                  onClick={() => setHandOutcomeDetailsExpanded((v) => !v)}
                  aria-expanded={handOutcomeDetailsExpanded}
                  className="mx-auto mt-4 inline-flex w-full max-w-md items-center justify-center gap-2 rounded-lg border border-white/18 bg-black/40 px-3 py-2 text-[11px] font-bold text-slate-100 transition hover:bg-black/55"
                >
                  {handOutcomeDetailsExpanded ? (
                    <>
                      <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                      Скрыть детали и историю
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                      Показать детали и историю
                    </>
                  )}
                </button>
              )}
              {handOutcomeDetailsExpanded ? (
                <div className="mx-auto mt-4 w-full max-w-md space-y-2 rounded-xl border border-white/12 bg-black/40 px-2.5 py-3 text-[11px] text-slate-200">
                  {outcomeSnapshots.length > 0 ? (
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-amber-200">Места и карты</p>
                      {outcomeSnapshots.map((row) => (
                        <div key={row.key} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/35 px-2 py-1.5">
                          <OnlineSeatAvatarBubble
                            isBot={Boolean(row.seatIsBot)}
                            displayName={row.name}
                            avatarUrl={row.avatarUrl}
                            frameClassName="h-9 w-9 shrink-0"
                            initialsClassName="text-[10px]"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-slate-100">
                              {row.name}
                              {row.isWinner ? <span className="ml-1 text-amber-200">• победа</span> : null}
                              {row.isFolded ? <span className="ml-1 text-rose-200">• пас</span> : null}
                            </p>
                            <p className="truncate font-mono text-[10px] text-slate-400">
                              {Array.isArray(row.cardStrings) && row.cardStrings.length > 0 ? row.cardStrings.join(' ') : '—'}
                            </p>
                          </div>
                          <span className="max-w-[48%] truncate text-right text-[10px] text-amber-100">
                            {row.handDescription || '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {outcomeDetailMoves.length > 0 ? (
                    <div className="max-h-[18vh] overflow-y-auto rounded-lg border border-white/10 bg-black/30 px-2 py-1.5">
                      <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-cyan-100">Ходы раздачи</p>
                      <div className="space-y-1 font-mono text-[10px] leading-relaxed text-slate-200">
                        {outcomeDetailMoves.slice(-14).map((m, idx) => {
                          const name = typeof m?.playerName === 'string' && m.playerName ? m.playerName : '—';
                          return (
                            <p key={`out-act-${idx}-${name}`}>
                              {name}: {formatHistoryAction(m)}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  <div className="max-h-[min(26vh,150px)] overflow-y-auto rounded-md border border-white/8 bg-black/20 px-2 py-1.5 font-mono text-[10px] leading-relaxed whitespace-pre-wrap">
                    {latestHistoryEntry
                      ? buildSimpleSekaOutcomeExportText([], latestHistoryEntry, { heroUserId })
                      : buildOnlineHandOutcomeExportText({
                          tableName: table?.name,
                          handSeq: gameState.handSeq ?? 0,
                          pot: gameState.pot,
                          winners: gameState.winners,
                          winner: gameState.winner,
                          handMoves,
                          heroUserId,
                          historyEntry: null,
                        })}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {incomingInvite?.id ? (
        <div className="pointer-events-none fixed right-3 top-20 z-[140] sm:right-5">
          <div
            ref={inviteBubbleRef}
            className="pointer-events-auto w-[min(90vw,320px)] rounded-xl border border-emerald-400/50 bg-gradient-to-b from-[#0f1f1b]/95 to-[#08110f]/95 p-2 shadow-[0_10px_28px_rgba(0,0,0,0.55)]"
          >
            <div className="flex items-center gap-2">
              <OnlineSeatAvatarBubble
                isBot={false}
                displayName={incomingInvite.fromUsername || 'Игрок'}
                avatarUrl={incomingInvite.fromAvatarUrl}
                frameClassName="h-8 w-8"
                initialsClassName="text-[9px]"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] font-bold text-emerald-100">
                  {incomingInvite.fromUsername || 'Игрок'} вызывает на {incomingInvite.stake || 0}
                </p>
                <p className="truncate text-[10px] text-slate-300">[Принять] [X]</p>
              </div>
              <button
                type="button"
                onClick={() => sendInviteDecisionFromGame(incomingInvite, 'declined')}
                className="rounded-md border border-white/15 bg-black/30 px-1.5 py-0.5 text-[11px] font-bold text-slate-200"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <div className="mt-2 flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setShowInviteDecisionPicker((v) => !v)}
                className="flex-1 rounded-lg border border-emerald-400/45 bg-emerald-600/25 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-100"
              >
                Принять
              </button>
            </div>
            {showInviteDecisionPicker ? (
              <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => sendInviteDecisionFromGame(incomingInvite, 'accept-now')}
                  className="rounded-md border border-cyan-400/40 bg-cyan-700/20 px-2 py-1 text-[10px] font-semibold text-cyan-100"
                >
                  Выйти сейчас
                </button>
                <button
                  type="button"
                  onClick={() => sendInviteDecisionFromGame(incomingInvite, 'accept-after-hand')}
                  className="rounded-md border border-amber-400/40 bg-amber-700/20 px-2 py-1 text-[10px] font-semibold text-amber-100"
                >
                  После раздачи
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {notifications.map((notification) =>
        notification.kind === 'svarka_auto' ? (
          <OnlineCurrencyToast
            key={notification.id}
            message={notification.message}
            kind="info"
            onClose={() => removeNotification(notification.id)}
          />
        ) : (
          <NotificationToast
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() => removeNotification(notification.id)}
          />
        )
      )}

      {sessionOutcomeOverlay || matchAbortOverlayOutcome ? (
        <SessionResultOverlay
          outcome={matchAbortOverlayOutcome || sessionOutcomeOverlay}
          onClose={() => {
            if (matchAbortOverlayOutcome) {
              winnerFanfarePlayAfterSessionOverlayCloseRef.current = false;
              completeMatchAbortDismiss();
              return;
            }
            if (graceExitLeaveAfterOverlayRef.current) {
              graceExitLeaveAfterOverlayRef.current = false;
              winnerFanfarePlayAfterSessionOverlayCloseRef.current = false;
              try {
                gameTransportRef.current?.disconnect?.();
              } catch {
                /* ignore */
              }
              handleLeaveTable?.({ targetView: 'lobby' });
              setSessionOutcomeOverlay(null);
              return;
            }
            if (winnerFanfarePlayAfterSessionOverlayCloseRef.current && !winnerFanfarePlayedForHandRef.current) {
              winnerFanfarePlayAfterSessionOverlayCloseRef.current = false;
              initAudio();
              setTimeout(() => {
                play('winner-game', 1);
                winnerFanfarePlayedForHandRef.current = true;
              }, 120);
            } else {
              winnerFanfarePlayAfterSessionOverlayCloseRef.current = false;
            }
            setSessionOutcomeOverlay(null);
          }}
        />
      ) : null}

      {showFlyingCoins && flyingCoins && flyingCoins.from && flyingCoins.to ? (
        <FlyingCoins
          amount={flyingCoins.amount}
          fromElement={flyingCoins.from}
          toElement={flyingCoins.to}
          onComplete={() => {
            setShowFlyingCoins(false);
            setFlyingCoins(null);
          }}
        />
      ) : null}

      <OnlineSekaLeaveModal
        open={leaveModalOpen}
        onClose={() => setLeaveModalOpen(false)}
        onConfirm={handleConfirmLeaveOnline}
        exactLossSeka={leaveModalHeroExposure}
        heroChips={leaveModalHeroChips}
        heroStreetBet={leaveModalHeroStreetBet}
        sessionStartStack={leaveModalBuyIn}
      />

      <TableSeatProfileModal
        open={seatProfileOpen != null}
        onClose={() => setSeatProfileOpen(null)}
        variant={seatProfileOpen?.variant || 'self'}
        displayName={seatProfileOpen?.displayName}
        chips={seatProfileOpen?.chips}
        userId={seatProfileOpen?.userId}
        initialAvatarUrl={seatProfileOpen?.avatarUrl}
        accessToken={accessToken}
        apiBaseUrl={getApiBaseUrl()}
        currentUserId={user?.id}
        onSendFriendRequest={onSendFriendRequest}
      />
    </div>
  );
}

function GameTable(props) {
  return (
    <AudioProvider>
      <GameTableInner {...props} />
    </AudioProvider>
  );
}

export default GameTable;
